import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { API_BASE_URL } from '../config';
import ThemeToggle from '../components/ThemeToggle';
import TrialBanner from '../components/TrialBanner';
import ReadOnlyBanner from '../components/ReadOnlyBanner';
import OptimizedImage from '../components/OptimizedImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { statusOptions, priorityOptions } from '../mock';
import { Plus, Search, Filter, ChevronDown, Home, Trash2, Calendar, Loader2, LogOut, Edit2, X, Table, Columns3, CalendarDays, Users, Settings, Bell, MessageSquare, Activity, Send, CheckSquare, Square, MessageCircle, BarChart3, GitBranch, Zap, Download, FileText, FileSpreadsheet, Github, Shield, Move, Webhook, Sparkles, Repeat, Clock, FileText as NotesIcon, Share2, Copy, Check } from 'lucide-react';
import BoardIcon, { BOARD_ICONS } from '../components/BoardIcon';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import KanbanBoard from '../components/KanbanBoard';
import ChatIntegrated from '../components/ChatIntegrated';
import BoardMetrics from '../components/BoardMetrics';
import BoardFilters from '../components/BoardFilters';
import GanttView from '../components/GanttView';
import PipelineView from '../components/PipelineView';
import AutomationsTab from '../components/AutomationsTab';
import WebhooksSettings from '../components/WebhooksSettings';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import AIPanel from '../components/AIPanel';
import BoardNotes from '../components/BoardNotes';
import TimeTracker, { TimerButton } from '../components/TimeTracker';
import BoardTypeSelector from '../components/BoardTypeSelector';
import ArchiveButton from '../components/ArchiveButton';
import DateRangePicker from '../components/DateRangePicker';
import GitHubIntegration from '../components/GitHubIntegration';
import { insertColumnBeforeDone } from '../utils/columnUtils';

const BACKEND_URL = API_BASE_URL;
const API = `${BACKEND_URL}/api`;

// Utility function to ensure status/priority objects have all required fields
const ensureValueField = (obj) => {
  if (!obj) return obj;
  if (obj.value) return obj; // Already has value field
  // Generate value from label (lowercase, replace spaces with underscores)
  const value = obj.label ? obj.label.toLowerCase().replace(/\s+/g, '_') : '';
  return { ...obj, value };
};

const Board = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { fetchSubscriptionStatus, canEdit, isTrialActive, isExpired } = useSubscription();
  
  // Get workspace and board from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const urlWorkspaceId = searchParams.get('workspace');
  const urlBoardId = searchParams.get('board');
  const urlView = searchParams.get('view'); // 'chat' or null
  
  // Workspace state
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'owner', 'admin', 'member'
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [invitationLink, setInvitationLink] = useState('');
  const [showInvitationSuccess, setShowInvitationSuccess] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [showAutomationsPanel, setShowAutomationsPanel] = useState(false);
  const [showWebhooksPanel, setShowWebhooksPanel] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showTimePanel, setShowTimePanel] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showGitHubPanel, setShowGitHubPanel] = useState(false);
  const [changingRoleForMember, setChangingRoleForMember] = useState(null);
  
  // Force close all modals on mount to prevent blocking
  useEffect(() => {
    setShowMembersPanel(false);
    setShowAutomationsPanel(false);
    setShowWebhooksPanel(false);
    setShowAnalytics(false);
    setShowGitHubPanel(false);
    setShowInvitationSuccess(false);
    setShowAssignModal(false);
    setShowEditDialog(false);
  }, []);
  
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [tasksCache, setTasksCache] = useState({}); // Cache: { boardId: tasks[] }
  const [newTask, setNewTask] = useState('');
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  
  // Filters state
  const [filters, setFilters] = useState({
    status: [],
    assignee: [],
    dateRange: { start: '', end: '' }
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Board creation state
  const [showBoardDialog, setShowBoardDialog] = useState(false);
  const [showBoardTypeSelector, setShowBoardTypeSelector] = useState(false);
  const [selectedBoardType, setSelectedBoardType] = useState('project');
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardIcon, setNewBoardIcon] = useState('briefcase');
  const [newBoardColor, setNewBoardColor] = useState('#4A90A4');
  
  // Task editing state
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Inline task name editing
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState('');
  
  // Task assignment state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  
  // Comments & Activity state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  
  // Mention autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('all'); // 'all', 'mentions', 'assigned'
  
  // View state
  const [currentView, setCurrentView] = useState('table'); // 'table', 'kanban', 'calendar'
  
  // Debug: Log when currentView changes
  React.useEffect(() => {
    console.log('currentView changed to:', currentView);
  }, [currentView]);
  
  // Calendar state
  const [showCalendarTaskDialog, setShowCalendarTaskDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarTaskName, setCalendarTaskName] = useState('');
  const [calendarMode, setCalendarMode] = useState('month'); // 'month' or 'week'
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());

  const [calendarView, setCalendarView] = useState('month'); // 'month' or 'day'
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  
  // Filter state (already declared above at line 103)
  // Keeping filters structure for backwards compatibility
  const [oldFilters, setOldFilters] = useState({
    status: [],
    assignee: [],
    dateRange: { start: '', end: '' }
  });

  // Subtask state
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [showSubtaskEdit, setShowSubtaskEdit] = useState(false);
  
  // Chat View State
  const [showChatView, setShowChatView] = useState(false);
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());
  
  // Invitation popup state
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showInvitationPopup, setShowInvitationPopup] = useState(false);

  // Fetch workspaces on mount
  useEffect(() => {
    fetchWorkspaces();
  }, []);

  // Fetch boards when workspace changes
  useEffect(() => {
    if (currentWorkspaceId) {
      fetchBoards();
      fetchWorkspaceMembers();
    }
  }, [currentWorkspaceId]);
  
  // Select board from URL after boards are loaded
  useEffect(() => {
    if (urlBoardId && boards.length > 0 && !selectedBoardId) {
      const boardExists = boards.find(b => b._id === urlBoardId);
      if (boardExists) {
        setSelectedBoardId(urlBoardId);
      } else if (boards.length > 0) {
        // If URL board doesn't exist, select first board
        setSelectedBoardId(boards[0]._id);
      }
    } else if (!selectedBoardId && boards.length > 0 && !urlBoardId) {
      // No URL board, select first board by default
      setSelectedBoardId(boards[0]._id);
    }
  }, [boards, urlBoardId]);

  // Fetch tasks when board changes
  useEffect(() => {
    if (selectedBoardId && currentWorkspaceId) {
      fetchTasks(selectedBoardId);
    }
  }, [selectedBoardId, currentWorkspaceId]);

  // Fetch notifications on mount and poll every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch pending invitations on mount
  useEffect(() => {
    if (user) {
      fetchPendingInvitations();
    }
  }, [user]);
  
  // Auto-open chat if view=chat in URL
  useEffect(() => {
    if (urlView === 'chat') {
      setShowChatView(true);
      setSelectedBoardId(null);
    }
  }, [urlView]);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(response.data);
      
      // Set workspace from URL or default to first
      if (response.data.length > 0) {
        const targetWorkspace = urlWorkspaceId 
          ? response.data.find(w => w._id === urlWorkspaceId)
          : response.data[0];
        
        if (targetWorkspace) {
          setCurrentWorkspaceId(targetWorkspace._id);
          setCurrentWorkspace(targetWorkspace);
          setUserRole(getUserRole(targetWorkspace));
          
          // Fetch subscription status
          await fetchSubscriptionStatus(targetWorkspace._id);
          
          // Board selection will happen in separate useEffect after boards are loaded
        }
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      toast({ title: 'Error fetching workspaces', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user's role in workspace
  const getUserRole = (workspace) => {
    if (!workspace || !user) return null;
    if (workspace.owner_id === user._id) return 'owner';
    const member = workspace.members?.find(m => m.user_id === user._id);
    return member?.role || null;
  };

  const fetchWorkspaceMembers = async () => {
    if (!currentWorkspaceId) return;
    try {
      const response = await axios.get(`${API}/workspaces/${currentWorkspaceId}/members`, { withCredentials: true });
      setWorkspaceMembers(response.data);
    } catch (error) {
      console.error('Error fetching workspace members:', error);
    }
  };

  const fetchComments = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/comments`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchActivity = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/activity`, { withCredentials: true });
      setActivityLog(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
      
      // Get unread count
      const countResponse = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(countResponse.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchBoards = async () => {
    if (!currentWorkspaceId) return;
    try {
      const response = await axios.get(`${API}/boards?workspace_id=${currentWorkspaceId}`, { withCredentials: true });
      setBoards(response.data);
      if (response.data.length > 0 && !selectedBoardId) {
        setSelectedBoardId(response.data[0]._id);
      }
      
      // Preload tasks for all boards in background for instant navigation
      response.data.forEach((board, index) => {
        // Stagger preloading to avoid overwhelming the server
        setTimeout(() => {
          if (!tasksCache[board._id]) {
            axios.get(`${API}/tasks?boardId=${board._id}&workspace_id=${currentWorkspaceId}`, { withCredentials: true })
              .then(res => {
                setTasksCache(prev => ({ ...prev, [board._id]: res.data }));
              })
              .catch(err => console.error('Preload error:', err));
          }
        }, index * 300); // Stagger by 300ms
      });
    } catch (error) {
      console.error('Error fetching boards:', error);
      toast({ title: 'Error fetching boards', variant: 'destructive' });
    }
  };

  const fetchTasks = async (boardId) => {
    if (!currentWorkspaceId) return;
    
    // Optimistic navigation - use cached data immediately if available
    if (tasksCache[boardId]) {
      setTasks(tasksCache[boardId]);
      setTasksLoading(false);
    } else {
      setTasksLoading(true);
    }

    try {
      const response = await axios.get(`${API}/tasks?boardId=${boardId}&workspace_id=${currentWorkspaceId}`, { withCredentials: true });
      setTasks(response.data);
      // Update cache
      setTasksCache(prev => ({ ...prev, [boardId]: response.data }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Don't show toast if we have cached data
      if (!tasksCache[boardId]) {
        toast({ title: 'Error fetching tasks', variant: 'destructive' });
      }
    } finally {
      setTasksLoading(false);
    }
  };

  const selectedBoard = boards.find(board => board._id === selectedBoardId);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters to tasks (with contextual filters based on board type)
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Basic filters (existing)
      if (filters.status && filters.status.length > 0 && !filters.status.includes(task.status.label)) {
        return false;
      }
      
      if (filters.assignee && filters.assignee.length > 0 && !filters.assignee.includes(task.person?.name)) {
        return false;
      }
      
      if (filters.dateRange && filters.dateRange.start && task.date < filters.dateRange.start) {
        return false;
      }
      if (filters.dateRange && filters.dateRange.end && task.date > filters.dateRange.end) {
        return false;
      }
      
      // Contextual filters based on board type
      const boardType = selectedBoard?.boardType || 'project';
      
      // Project filters
      if (boardType === 'project') {
        if (filters.minProgress && (task.progress || 0) < parseFloat(filters.minProgress)) {
          return false;
        }
        if (filters.maxProgress && (task.progress || 0) > parseFloat(filters.maxProgress)) {
          return false;
        }
      }
      
      // Marketing filters
      if (boardType === 'marketing') {
        if (filters.channel && task.channel !== filters.channel) {
          return false;
        }
        if (filters.launchDate && task.launch_date !== filters.launchDate) {
          return false;
        }
      }
      
      // Sales filters
      if (boardType === 'sales') {
        if (filters.stage && task.status.value !== filters.stage) {
          return false;
        }
        if (filters.minDealValue && (task.deal_value || 0) < parseFloat(filters.minDealValue)) {
          return false;
        }
        if (filters.maxDealValue && (task.deal_value || 0) > parseFloat(filters.maxDealValue)) {
          return false;
        }
        if (filters.company && !task.company?.toLowerCase().includes(filters.company.toLowerCase())) {
          return false;
        }
      }
      
      return true;
    });
  }, [tasks, filters, selectedBoard]);

  // Helper to generate color for member
  const getMemberColor = (name) => {
    const colors = ['#1C3E53', '#4A90A4', '#00D4AA', '#FDAB3D', '#E44258', '#00C875', '#579BFC'];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  // Helper to get current board's status options
  const getCurrentStatusOptions = () => {
    let statuses = selectedBoard?.custom_statuses && selectedBoard.custom_statuses.length > 0
      ? [...selectedBoard.custom_statuses]
      : [...statusOptions];
    
    // Ensure "Done" column is always last
    const doneIndex = statuses.findIndex(s => 
      s.label.toLowerCase() === 'done' || 
      s.label.toLowerCase() === 'fini' || 
      s.label.toLowerCase() === 'terminé' ||
      s.value === 'done'
    );
    
    if (doneIndex !== -1 && doneIndex !== statuses.length - 1) {
      const doneColumn = statuses.splice(doneIndex, 1)[0];
      statuses.push(doneColumn);
    }
    
    return statuses;
  };

  // Get unique assignees for filter
  const uniqueAssignees = useMemo(() => {
    return [...new Set(tasks.map(t => t.person?.name).filter(Boolean))];
  }, [tasks]);

  const handleClearFilters = () => {
    setFilters({
      status: [],
      assignee: [],
      dateRange: { start: '', end: '' }
    });
  };
  
  // Inline task name editing functions
  const startEditingTaskName = (taskId, currentName) => {
    setEditingTaskId(taskId);
    setEditingTaskName(currentName);
  };

  const cancelEditingTaskName = () => {
    setEditingTaskId(null);
    setEditingTaskName('');
  };

  const saveTaskName = async (taskId) => {
    if (!editingTaskName.trim()) {
      cancelEditingTaskName();
      return;
    }

    try {
      await handleTaskUpdate(taskId, { task: editingTaskName });
      cancelEditingTaskName();
    } catch (error) {
      console.error('Error updating task name:', error);
      cancelEditingTaskName();
    }
  };
  
  // Column reordering handler
  const handleColumnReorder = async (newColumns) => {
    try {
      await axios.put(
        `${API}/boards/${selectedBoardId}/statuses/reorder`,
        { statuses: newColumns },
        { withCredentials: true }
      );
      await fetchBoards(); // Refresh to get updated order
      toast({ title: 'Columns reordered successfully!' });
    } catch (error) {
      console.error('Error reordering columns:', error);
      toast({ title: 'Error reordering columns', variant: 'destructive' });
    }
  };

  const hasActiveFilters = (filters.status?.length > 0) || (filters.assignee?.length > 0) || filters.dateRange?.start || filters.dateRange?.end;

  const handleAddTask = async () => {
    if (!canEdit()) {
      toast({ 
        title: 'Mode lecture seule', 
        description: 'Passez à la version payante pour modifier',
        variant: 'destructive' 
      });
      return;
    }
    
    if (!newTask.trim()) return;
    if (!currentWorkspaceId) {
      toast({ title: 'No workspace selected', variant: 'destructive' });
      return;
    }

    // Get first status column
    const currentStatuses = getCurrentStatusOptions();
    const firstStatus = currentStatuses[0] || { value: 'to_do', label: 'To Do', color: '#C4C4C4' };

    const newTaskObj = {
      boardId: selectedBoardId,
      task: newTask,
      assignees: [{ name: 'Unassigned', avatar: 'UN', color: '#CCCCCC' }],
      status: ensureValueField(firstStatus),
      priority: { value: 'medium', label: 'Medium', color: '#FDAB3D' },
      date: new Date().toISOString().split('T')[0],
      timeline: '',
      notes: '',
      images: []
    };

    // Optimistic update - add task immediately with temporary ID
    const tempId = 'temp_' + Date.now();
    const optimisticTask = { 
      ...newTaskObj, 
      _id: tempId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updatedTasks = [...tasks, optimisticTask];
    setTasks(updatedTasks);
    setNewTask('');
    
    // Update cache immediately
    if (selectedBoardId) {
      setTasksCache(prev => ({ ...prev, [selectedBoardId]: updatedTasks }));
    }

    try {
      const response = await axios.post(`${API}/tasks?workspace_id=${currentWorkspaceId}`, newTaskObj, { withCredentials: true });
      // Replace temp task with real one from server
      const finalTasks = updatedTasks.map(task => task._id === tempId ? response.data : task);
      setTasks(finalTasks);
      // Update cache with real task
      if (selectedBoardId) {
        setTasksCache(prev => ({ ...prev, [selectedBoardId]: finalTasks }));
      }
    } catch (error) {
      // Remove temp task on error
      setTasks(prevTasks => prevTasks.filter(task => task._id !== tempId));
      if (selectedBoardId) {
        setTasksCache(prev => ({ ...prev, [selectedBoardId]: tasks }));
      }
      console.error('Error adding task:', error);
      toast({ title: 'Error adding task', variant: 'destructive' });
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!canEdit()) {
      toast({ 
        title: 'Mode lecture seule', 
        description: 'Passez à la version payante pour supprimer',
        variant: 'destructive' 
      });
      return;
    }
    
    // Confirmation dialog
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }
    
    // Optimistic update - remove immediately for smooth UX
    const previousTasks = [...tasks];
    const updatedTasks = tasks.filter(task => task._id !== taskId);
    setTasks(updatedTasks);
    
    // Update cache immediately
    if (selectedBoardId) {
      setTasksCache(prev => ({ ...prev, [selectedBoardId]: updatedTasks }));
    }

    try {
      await axios.delete(`${API}/tasks/${taskId}`, { withCredentials: true });
      toast({ title: 'Task deleted successfully' });
      // Success - keep the optimistic update
    } catch (error) {
      // Rollback on error
      setTasks(previousTasks);
      if (selectedBoardId) {
        setTasksCache(prev => ({ ...prev, [selectedBoardId]: previousTasks }));
      }
      console.error('Error deleting task:', error);
      toast({ title: 'Error deleting task', variant: 'destructive' });
    }
  };

  const handleTaskUpdate = async (taskId, updateData) => {
    // Optimistic UI Update - Update immediately for smooth UX
    const previousTasks = [...tasks];
    const updatedTasks = tasks.map(task => 
      task._id === taskId 
        ? { ...task, ...updateData, updatedAt: new Date().toISOString() }
        : task
    );
    setTasks(updatedTasks);
    
    // Update cache immediately for instant navigation
    if (selectedBoardId) {
      setTasksCache(prev => ({ ...prev, [selectedBoardId]: updatedTasks }));
    }

    try {
      // Send update to backend in background
      const response = await axios.put(`${API}/tasks/${taskId}`, updateData, { withCredentials: true });
      // Update with server response to ensure consistency
      const finalTasks = updatedTasks.map(task => task._id === taskId ? response.data : task);
      setTasks(finalTasks);
      // Update cache with server response
      if (selectedBoardId) {
        setTasksCache(prev => ({ ...prev, [selectedBoardId]: finalTasks }));
      }
    } catch (error) {
      // Rollback on error
      setTasks(previousTasks);
      if (selectedBoardId) {
        setTasksCache(prev => ({ ...prev, [selectedBoardId]: previousTasks }));
      }
      console.error('Error updating task:', error);
      toast({ title: 'Error updating task', variant: 'destructive' });
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    handleTaskUpdate(taskId, { status: ensureValueField(newStatus) });
  };

  const handlePriorityChange = (taskId, newPriority) => {
    handleTaskUpdate(taskId, { priority: ensureValueField(newPriority) });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      toast({ title: 'Please enter a workspace name', variant: 'destructive' });
      return;
    }

    try {
      const response = await axios.post(`${API}/workspaces`, { name: newWorkspaceName }, { withCredentials: true });
      const newWorkspace = response.data;
      
      // Add to workspaces list
      setWorkspaces([...workspaces, newWorkspace]);
      
      // Auto-select the new workspace
      setCurrentWorkspaceId(newWorkspace._id);
      setCurrentWorkspace(newWorkspace);
      setUserRole(getUserRole(newWorkspace));
      
      // Fetch boards and members for the new workspace
      await fetchBoards();
      await fetchWorkspaceMembers();
      
      setNewWorkspaceName('');
      setShowCreateWorkspace(false);
      toast({ title: 'Workspace created and selected!' });
    } catch (error) {
      console.error('Error creating workspace:', error);
      toast({ title: 'Error creating workspace', variant: 'destructive' });
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast({ title: 'Please enter an email address', variant: 'destructive' });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/workspaces/${currentWorkspaceId}/invite`,
        { email: inviteEmail, role: inviteRole },
        { withCredentials: true }
      );
      
      // Generate invitation link
      const frontendUrl = window.location.origin;
      const link = `${frontendUrl}/auth?invite=${response.data.token}`;
      setInvitationLink(link);
      
      // Show success modal with link
      setShowInviteModal(false);
      setShowInvitationSuccess(true);
      
      fetchWorkspaceMembers();
      
      toast({ 
        title: 'Invitation created!',
        description: response.data.user_exists 
          ? 'User has an account. They can use this link to join the workspace.'
          : 'User needs to create an account. Share the link to get them started.'
      });
    } catch (error) {
      console.error('Error inviting member:', error);
      const errorMessage = error.response?.data?.detail || 'Error inviting member';
      toast({ 
        title: 'Failed to create invitation',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(invitationLink);
    toast({ title: 'Invitation link copied to clipboard!' });
  };

  const handleWorkspaceChange = async (workspaceId) => {
    setCurrentWorkspaceId(workspaceId);
    const workspace = workspaces.find(w => w._id === workspaceId);
    setCurrentWorkspace(workspace);
    setUserRole(getUserRole(workspace));
  };

  const handleChangeMemberRole = async (memberId, newRole) => {
    try {
      await axios.put(
        `${API}/workspaces/${currentWorkspaceId}/members/${memberId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      
      // Update local state
      setWorkspaceMembers(workspaceMembers.map(m => 
        m._id === memberId ? { ...m, role: newRole } : m
      ));
      
      toast({ title: `Member role updated to ${newRole}` });
    } catch (error) {
      console.error('Error changing member role:', error);
      toast({ title: error.response?.data?.detail || 'Error changing member role', variant: 'destructive' });
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this workspace? They will lose access to all boards and tasks.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/workspaces/${currentWorkspaceId}/members/${memberId}`,
        { withCredentials: true }
      );
      
      // Update local state
      setWorkspaceMembers(workspaceMembers.filter(m => m._id !== memberId));
      
      toast({ title: `${memberName} removed from workspace` });
    } catch (error) {
      console.error('Error removing member:', error);
      toast({ title: error.response?.data?.detail || 'Error removing member', variant: 'destructive' });
    }
  };

  const canRemoveMember = (member) => {
    // Members can't remove anyone
    if (userRole === 'member') return false;
    
    // Can't remove owner
    if (member.role === 'owner') return false;
    
    // Can't remove yourself
    if (member._id === user?._id) return false;
    
    // Admins can only remove members (not other admins)
    if (userRole === 'admin' && member.role === 'admin') return false;
    
    // Owners can remove anyone (except themselves and owner, already checked)
    return true;
  };

  const handleShareBoard = async () => {
    if (!selectedBoard) return;
    try {
      const r = await axios.post(`${API}/boards/${selectedBoard._id}/share`, {}, { withCredentials: true });
      const link = `${window.location.origin}/share/${r.data.share_token}`;
      setShareLink(link);
      setShowShareModal(true);
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Erreur partage', variant: 'destructive' });
    }
  };

  const handleRevokeShare = async () => {
    if (!selectedBoard) return;
    try {
      await axios.delete(`${API}/boards/${selectedBoard._id}/share`, { withCredentials: true });
      setShareLink(null);
      setShowShareModal(false);
      toast({ title: 'Lien de partage révoqué' });
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const handleOpenAssignModal = (task) => {
    setTaskToAssign(task);
    // Filter out "Unassigned" when opening modal
    const realAssignees = (task.assignees || []).filter(a => a.name !== 'Unassigned');
    setSelectedAssignees(realAssignees);
    setShowAssignModal(true);
  };

  const handleToggleAssignee = (member) => {
    const isSelected = selectedAssignees.some(a => a.name === member.name);
    if (isSelected) {
      setSelectedAssignees(selectedAssignees.filter(a => a.name !== member.name));
    } else {
      const newAssignee = {
        name: member.name,
        avatar: member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase(),
        color: member.color || '#1C3E53'
      };
      // Remove "Unassigned" when adding real assignees
      const filteredAssignees = selectedAssignees.filter(a => a.name !== 'Unassigned');
      setSelectedAssignees([...filteredAssignees, newAssignee]);
    }
  };

  const handleSaveAssignments = async () => {
    if (!taskToAssign) return;
    
    try {
      // If no assignees selected, add "Unassigned"
      const finalAssignees = selectedAssignees.length === 0 
        ? [{ name: 'Unassigned', avatar: 'UN', color: '#CCCCCC' }]
        : selectedAssignees;
      
      await handleTaskUpdate(taskToAssign._id, { assignees: finalAssignees });
      setShowAssignModal(false);
      setTaskToAssign(null);
      setSelectedAssignees([]);
      toast({ title: 'Task assignees updated successfully!' });
    } catch (error) {
      console.error('Error updating assignees:', error);
      toast({ title: 'Error updating assignees', variant: 'destructive' });
    }
  };

  const handleFileUpload = async (files) => {
    if (!editingTask || uploading) return;
    
    const currentAttachments = editingTask.attachments || [];
    if (currentAttachments.length >= 10) {
      toast({ title: 'Maximum 10 attachments per task', variant: 'destructive' });
      return;
    }
    
    setUploading(true);
    let uploadedCount = 0;
    const filesToUpload = Array.from(files);
    
    for (const file of filesToUpload) {
      // Validate file size
      if (file.size > 10 * 1024 * 1024) {
        toast({ title: `${file.name} exceeds 10MB limit`, variant: 'destructive' });
        continue;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({ title: `${file.name} is not a supported image format`, variant: 'destructive' });
        continue;
      }
      
      // Read file as base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const response = await axios.post(
            `${API}/tasks/${editingTask._id}/upload-attachment`,
            {
              file_data: reader.result,
              file_name: file.name,
              file_size: file.size,
              mime_type: file.type
            },
            { withCredentials: true }
          );
          
          // Update task with new attachment
          const updatedAttachments = [...(editingTask.attachments || []), response.data.attachment];
          setEditingTask({...editingTask, attachments: updatedAttachments});
          uploadedCount++;
          toast({ title: `${file.name} uploaded successfully!` });
          
          // If all files uploaded, refresh tasks and reset uploading state
          if (uploadedCount === filesToUpload.length) {
            await fetchTasks(selectedBoardId);
            setUploading(false);
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          toast({ title: error.response?.data?.detail || 'Error uploading file', variant: 'destructive' });
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!editingTask) return;
    
    try {
      await axios.delete(
        `${API}/tasks/${editingTask._id}/attachments/${attachmentId}`,
        { withCredentials: true }
      );
      
      const updatedAttachments = editingTask.attachments.filter(a => a.id !== attachmentId);
      setEditingTask({...editingTask, attachments: updatedAttachments});
      toast({ title: 'Attachment deleted successfully!' });
      
      // Refresh tasks
      await fetchTasks(selectedBoardId);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast({ title: 'Error deleting attachment', variant: 'destructive' });
    }
  };



  const handleBoardTypeSelected = (boardType) => {
    setSelectedBoardType(boardType);
    setShowBoardTypeSelector(false);
    setShowBoardDialog(true);
  };

  const handleCreateBoard = async () => {
    if (!newBoardName.trim()) {
      toast({ title: 'Please enter a board name', variant: 'destructive' });
      return;
    }

    if (!currentWorkspaceId) {
      toast({ title: 'Please select a workspace first', variant: 'destructive' });
      return;
    }

    try {
      const defaultColumns = selectedBoardType === 'marketing' 
        ? [
            {"value": "planning", "label": "Planning", "color": "#A25DDC"},
            {"value": "in_production", "label": "In Production", "color": "#FDAB3D"},
            {"value": "published", "label": "Published", "color": "#00C875"}
          ]
        : selectedBoardType === 'sales'
        ? [
            {"value": "lead", "label": "Lead", "color": "#C4C4C4"},
            {"value": "contacted", "label": "Contacted", "color": "#579BFC"},
            {"value": "proposal", "label": "Proposal", "color": "#FDAB3D"},
            {"value": "won", "label": "Won", "color": "#00C875"}
          ]
        : [
            {"value": "to_do", "label": "To Do", "color": "#C4C4C4"},
            {"value": "in_progress", "label": "In Progress", "color": "#FDAB3D"},
            {"value": "done", "label": "Done", "color": "#00C875"}
          ];

      const response = await axios.post(
        `${API}/boards?workspace_id=${currentWorkspaceId}`,
        {
          name: newBoardName,
          icon: newBoardIcon,
          color: newBoardColor,
          boardType: selectedBoardType,
          custom_statuses: defaultColumns
        },
        { withCredentials: true }
      );
      
      setBoards([...boards, response.data]);
      setShowBoardDialog(false);
      setNewBoardName('');
      setNewBoardIcon('briefcase');
      setNewBoardColor('#4A90A4');
      setSelectedBoardType('project');
      toast({ title: 'Board created successfully!' });
    } catch (error) {
      console.error('Error creating board:', error);
      toast({ title: 'Error creating board', variant: 'destructive' });
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (!window.confirm('Are you sure you want to delete this board? All tasks will be deleted.')) {
      return;
    }

    try {
      await axios.delete(`${API}/boards/${boardId}`, { withCredentials: true });
      setBoards(boards.filter(board => board._id !== boardId));
      if (selectedBoardId === boardId) {
        setSelectedBoardId(boards[0]?._id || null);
      }
      toast({ title: 'Board deleted successfully!' });
    } catch (error) {
      console.error('Error deleting board:', error);
      toast({ title: 'Error deleting board', variant: 'destructive' });
    }
  };

  const handleEditTask = (task) => {
    setEditingTask({ ...task });
    setShowEditDialog(true);
    // Fetch comments and activity when opening task
    fetchComments(task._id);
    fetchActivity(task._id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      // Extract mentions from comment (look for @username patterns)
      const mentionRegex = /@(\w+)/g;
      const mentionedNames = [...newComment.matchAll(mentionRegex)].map(m => m[1]);
      
      // Find user IDs for mentioned users
      const mentionedUserIds = workspaceMembers
        .filter(member => mentionedNames.includes(member.name))
        .map(member => member.user_id);

      const response = await axios.post(
        `${API}/tasks/${editingTask._id}/comments`,
        { 
          task_id: editingTask._id,
          content: newComment,
          mentions: mentionedUserIds
        },
        { withCredentials: true }
      );
      
      setComments([...comments, response.data]);
      setNewComment('');
      setShowMentions(false);
      fetchActivity(editingTask._id); // Refresh activity log
      toast({ title: 'Comment added!' });
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({ title: 'Error adding comment', variant: 'destructive' });
    }
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    setNewComment(value);

    // Check if user typed @ symbol
    const textBeforeCursor = value.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && (lastAtIndex === 0 || value[lastAtIndex - 1] === ' ')) {
      const searchText = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Only show mentions if there's no space after @
      if (!searchText.includes(' ')) {
        setMentionSearch(searchText.toLowerCase());
        setShowMentions(true);
        setSelectedMentionIndex(0);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!mentionSearch) return workspaceMembers;
    return workspaceMembers.filter(member => 
      member.name.toLowerCase().includes(mentionSearch)
    );
  }, [workspaceMembers, mentionSearch]);

  const handleSelectMention = (member) => {
    const textBeforeCursor = newComment.substring(0, newComment.lastIndexOf('@'));
    const textAfterCursor = newComment.substring(newComment.lastIndexOf('@') + mentionSearch.length + 1);
    
    setNewComment(`${textBeforeCursor}@${member.name} ${textAfterCursor}`);
    setShowMentions(false);
    setMentionSearch('');
  };

  const handleMentionKeyDown = (e) => {
    if (!showMentions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev => 
        prev < filteredMembers.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter' && filteredMembers.length > 0) {
      e.preventDefault();
      handleSelectMention(filteredMembers[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editCommentText.trim()) return;

    try {
      const response = await axios.put(
        `${API}/tasks/${editingTask._id}/comments/${commentId}`,
        { 
          content: editCommentText,
          mentions: [] // Could implement mention extraction here too
        },
        { withCredentials: true }
      );
      
      setComments(comments.map(c => c._id === commentId ? response.data : c));
      setEditingCommentId(null);
      setEditCommentText('');
      toast({ title: 'Comment updated!' });
    } catch (error) {
      console.error('Error updating comment:', error);
      toast({ title: 'Error updating comment', variant: 'destructive' });
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await axios.delete(
        `${API}/tasks/${editingTask._id}/comments/${commentId}`,
        { withCredentials: true }
      );
      
      setComments(comments.filter(c => c._id !== commentId));
      toast({ title: 'Comment deleted!' });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({ title: 'Error deleting comment', variant: 'destructive' });
    }
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { withCredentials: true }
      );
      
      setNotifications(notifications.map(n => 
        n._id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast({ title: 'All notifications marked as read!' });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Subtask Handlers
  const handleAddSubtask = async (taskId) => {
    if (!newSubtaskTitle.trim()) return;
    
    try {
      setAddingSubtask(true);
      const response = await axios.post(
        `${API}/tasks/${taskId}/subtasks`,
        { title: newSubtaskTitle },
        { withCredentials: true }
      );
      
      // Update the task with new subtask
      setTasks(tasks.map(t => {
        if (t._id === taskId) {
          return {
            ...t,
            subtasks: [...(t.subtasks || []), response.data.subtask]
          };
        }
        return t;
      }));
      
      // Update editing task if it's the same
      if (editingTask && editingTask._id === taskId) {
        setEditingTask({
          ...editingTask,
          subtasks: [...(editingTask.subtasks || []), response.data.subtask]
        });
      }
      
      setNewSubtaskTitle('');
      toast({ title: 'Subtask added!' });
    } catch (error) {
      console.error('Error adding subtask:', error);
      toast({ title: 'Error adding subtask', variant: 'destructive' });
    } finally {
      setAddingSubtask(false);
    }
  };

  const handleToggleSubtask = async (taskId, subtaskId, completed) => {
    try {
      const response = await axios.put(
        `${API}/tasks/${taskId}/subtasks/${subtaskId}`,
        { completed: !completed },
        { withCredentials: true }
      );
      
      // Update task with updated subtask
      if (response.data.task) {
        setTasks(tasks.map(t => t._id === taskId ? response.data.task : t));
        if (editingTask && editingTask._id === taskId) {
          setEditingTask(response.data.task);
        }
      }
    } catch (error) {
      console.error('Error toggling subtask:', error);
      toast({ title: 'Error updating subtask', variant: 'destructive' });
    }
  };

  const handleDeleteSubtask = async (taskId, subtaskId) => {
    if (!window.confirm('Delete this subtask?')) return;
    
    try {
      await axios.delete(
        `${API}/tasks/${taskId}/subtasks/${subtaskId}`,
        { withCredentials: true }
      );
      
      // Update task by filtering out deleted subtask
      setTasks(tasks.map(t => {
        if (t._id === taskId) {
          return {
            ...t,
            subtasks: (t.subtasks || []).filter(s => s.id !== subtaskId)
          };
        }
        return t;
      }));
      
      if (editingTask && editingTask._id === taskId) {
        setEditingTask({
          ...editingTask,
          subtasks: (editingTask.subtasks || []).filter(s => s.id !== subtaskId)
        });
      }
      
      toast({ title: 'Subtask deleted!' });
    } catch (error) {
      console.error('Error deleting subtask:', error);
      toast({ title: 'Error deleting subtask', variant: 'destructive' });
    }
  };

  const handleEditSubtask = (taskId, subtask) => {
    setEditingSubtask({ taskId, ...subtask });
    setShowSubtaskEdit(true);
  };

  const handleUpdateSubtask = async () => {
    if (!editingSubtask) return;
    
    try {
      const updateData = {
        title: editingSubtask.title,
        assignee: editingSubtask.assignee,
        due_date: editingSubtask.due_date,
        priority: editingSubtask.priority,
        notes: editingSubtask.notes
      };
      
      const response = await axios.put(
        `${API}/tasks/${editingSubtask.taskId}/subtasks/${editingSubtask.id}`,
        updateData,
        { withCredentials: true }
      );
      
      if (response.data.task) {
        setTasks(tasks.map(t => t._id === editingSubtask.taskId ? response.data.task : t));
        if (editingTask && editingTask._id === editingSubtask.taskId) {
          setEditingTask(response.data.task);
        }
      }
      
      setShowSubtaskEdit(false);
      setEditingSubtask(null);
      toast({ title: 'Subtask updated!' });
    } catch (error) {
      console.error('Error updating subtask:', error);
      toast({ title: 'Error updating subtask', variant: 'destructive' });
    }
  };

  const toggleTaskExpansion = (taskId) => {
    const newExpanded = new Set(expandedTaskIds);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTaskIds(newExpanded);
  };

  const getSubtaskProgress = (task) => {
    const subtasks = task.subtasks?.filter(s => !s.deleted) || [];
    if (subtasks.length === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = subtasks.filter(s => s.completed).length;
    return {
      completed,
      total: subtasks.length,
      percentage: Math.round((completed / subtasks.length) * 100)
    };
  };


  // Invitation Handlers
  const fetchPendingInvitations = async () => {
    try {
      const response = await axios.get(`${API}/invitations/pending`, { withCredentials: true });
      setPendingInvitations(response.data);
      if (response.data.length > 0) {
        setShowInvitationPopup(true);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${API}/invitations/${invitationId}/accept`,
        {},
        { withCredentials: true }
      );
      toast({ title: '✅ Invitation accepted!', description: 'Workspace added to your sidebar' });
      setPendingInvitations(pendingInvitations.filter(i => i._id !== invitationId));
      fetchWorkspaces(); // Refresh workspaces
      fetchNotifications(); // Refresh notifications
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast({ 
        title: 'Error accepting invitation', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${API}/invitations/${invitationId}/reject`,
        {},
        { withCredentials: true }
      );
      toast({ title: 'Invitation declined' });
      setPendingInvitations(pendingInvitations.filter(i => i._id !== invitationId));
      fetchNotifications(); // Refresh notifications
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      toast({ title: 'Error rejecting invitation', variant: 'destructive' });
    }
  };


  const handleSaveTaskEdit = async () => {
    if (!canEdit()) {
      toast({ 
        title: 'Mode lecture seule', 
        description: 'Passez à la version payante pour modifier',
        variant: 'destructive' 
      });
      return;
    }
    
    if (!editingTask.task.trim()) {
      toast({ title: 'Task name cannot be empty', variant: 'destructive' });
      return;
    }

    const updatePayload = {
      task: editingTask.task,
      person: editingTask.person,
      assignees: editingTask.assignees,
      status: ensureValueField(editingTask.status),
      priority: ensureValueField(editingTask.priority),
      date: editingTask.date,
      timeline: editingTask.timeline,
      notes: editingTask.notes,
      budget: editingTask.budget,
      attachments: editingTask.attachments,
      // Sales board fields
      company: editingTask.company,
      deal_value: editingTask.deal_value,
      closing_date: editingTask.closing_date,
      next_action: editingTask.next_action,
      // Marketing board fields
      channel: editingTask.channel,
      performance: editingTask.performance,
      // Project board fields
      progress: editingTask.progress,
      start_date: editingTask.start_date,
      recurrence: editingTask.recurrence || null
    };

    // Optimistic update - close dialog immediately for smooth UX
    const previousTasks = [...tasks];
    setTasks(tasks.map(task => 
      task._id === editingTask._id 
        ? { ...task, ...updatePayload, updatedAt: new Date().toISOString() }
        : task
    ));
    setShowEditDialog(false);
    setEditingTask(null);

    try {
      const response = await axios.put(
        `${API}/tasks/${editingTask._id}`,
        updatePayload,
        { withCredentials: true }
      );
      
      // Update with server response
      setTasks(prevTasks => prevTasks.map(task => task._id === response.data._id ? response.data : task));
      toast({ title: 'Task updated successfully!' });
    } catch (error) {
      // Rollback on error
      setTasks(previousTasks);
      console.error('Error updating task:', error);
      toast({ title: 'Error updating task', variant: 'destructive' });
    }
  };

  // Render Kanban View
  const renderKanbanView = () => {
    // Get columns from board's custom statuses or use defaults
    const boardColumns = selectedBoard?.custom_statuses || statusOptions.map((s, i) => ({
      label: s.label,
      color: s.color,
      order: i,
      isCustom: false
    }));


    const handleUpdateTaskDate = async (taskId, newDate) => {
      await handleTaskUpdate(taskId, { date: newDate });
      // No toast for smoother UX - visual feedback is enough
    };

    const columns = boardColumns.map(col => ({
      ...col,
      isCustom: !!selectedBoard?.custom_statuses
    }));

    const handleTaskMove = async (taskId, newColumn) => {
      const newStatus = ensureValueField(newColumn);
      await handleTaskUpdate(taskId, { status: newStatus });
      // No toast for smoother UX - visual feedback is enough
    };

    const handleAddColumn = async (name, color) => {
      const value = name.toLowerCase().replace(/\s+/g, '_'); // Generate value from name
      const newColumn = { value, label: name, color, isCustom: true };
      
      // Insert before Done column using utility
      const newColumns = insertColumnBeforeDone(columns, newColumn);
      
      try {
        await axios.put(
          `${API}/boards/${selectedBoardId}/statuses`,
          newColumns,
          { withCredentials: true }
        );
        await fetchBoards(); // Refresh boards to get updated statuses
        await fetchTasks(selectedBoardId); // Refresh tasks so they have new status available
        toast({ title: 'Column added successfully! Placed before Done column.' });
      } catch (error) {
        console.error('Error adding column:', error);
        toast({ title: 'Error adding column', variant: 'destructive' });
      }
    };

    const handleEditColumn = async (oldLabel, newLabel, newColor) => {
      try {
        // Use the new rename endpoint that updates both board and all tasks
        await axios.put(
          `${API}/boards/${selectedBoardId}/statuses/rename`,
          { old_label: oldLabel, new_label: newLabel, new_color: newColor },
          { withCredentials: true }
        );
        
        // Refresh data to get updated status across all views
        await fetchBoards();
        await fetchTasks(selectedBoardId);
        
        toast({ title: 'Status renamed successfully across all views!' });
      } catch (error) {
        console.error('Error renaming status:', error);
        toast({ title: 'Error renaming status', variant: 'destructive' });
      }
    };

    const handleDeleteColumn = async (label) => {
      // Prevent deleting "Done" column
      const isDoneColumn = label.toLowerCase() === 'done' || 
                          label.toLowerCase() === 'fini' || 
                          label.toLowerCase() === 'terminé' ||
                          label.toLowerCase() === 'finished' ||
                          label.toLowerCase() === 'completed';
      
      if (isDoneColumn) {
        toast({ 
          title: 'Cannot delete Done column', 
          description: 'The Done column is required and cannot be deleted',
          variant: 'destructive' 
        });
        return;
      }
      
      if (!window.confirm(`Delete column "${label}"? Tasks in this column will be moved to the first column.`)) {
        return;
      }

      const updatedColumns = columns.filter(col => col.label !== label);
      
      try {
        await axios.put(
          `${API}/boards/${selectedBoardId}/statuses`,
          updatedColumns,
          { withCredentials: true }
        );
        
        // Move tasks to first column
        const tasksToMove = tasks.filter(t => t.status.label === label);
        const firstColumn = updatedColumns[0];
        for (const task of tasksToMove) {
          await handleTaskUpdate(task._id, { status: ensureValueField(firstColumn) });
        }
        
        fetchBoards(); // Refresh boards
        fetchTasks(selectedBoardId);
        toast({ title: 'Column deleted successfully!' });
      } catch (error) {
        console.error('Error deleting column:', error);
        toast({ title: 'Error deleting column', variant: 'destructive' });
      }
    };

    const handleAddTaskToColumn = async (columnLabel, taskName) => {
      if (!currentWorkspaceId) {
        toast({ title: 'No workspace selected', variant: 'destructive' });
        return;
      }

      const column = columns.find(c => c.label === columnLabel);
      const newTaskObj = {
        boardId: selectedBoardId,
        task: taskName,
        assignees: [{ name: 'Unassigned', avatar: 'UN', color: '#CCCCCC' }],
        status: ensureValueField(column),
        priority: { value: 'medium', label: 'Medium', color: '#FDAB3D' },
        date: new Date().toISOString().split('T')[0],
        timeline: 'Q1 2025',
        notes: ''
      };

      // Optimistic update - add immediately with temp ID
      const tempId = 'temp_' + Date.now();
      const optimisticTask = { 
        ...newTaskObj, 
        _id: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setTasks([...tasks, optimisticTask]);

      try {
        const response = await axios.post(`${API}/tasks?workspace_id=${currentWorkspaceId}`, newTaskObj, { withCredentials: true });
        // Replace temp task with real one
        setTasks(prevTasks => prevTasks.map(task => task._id === tempId ? response.data : task));
      } catch (error) {
        // Remove temp task on error
        setTasks(prevTasks => prevTasks.filter(task => task._id !== tempId));
        console.error('Error adding task:', error);
        toast({ title: 'Error adding task', variant: 'destructive' });
      }
    };

    return (
      <KanbanBoard
        tasks={filteredTasks}
        columns={columns}
        onTaskMove={handleTaskMove}
        onEditTask={handleEditTask}
        onDeleteTask={handleDeleteTask}
        onAddColumn={handleAddColumn}
        onEditColumn={handleEditColumn}
        onDeleteColumn={handleDeleteColumn}
        onAddTaskToColumn={handleAddTaskToColumn}
        onAssignTask={handleOpenAssignModal}
        onUpdateTaskDate={handleUpdateTaskDate}
        getSubtaskProgress={getSubtaskProgress}
        onColumnReorder={handleColumnReorder}
      />
    );
  };

  // Render Calendar View
  const renderCalendarView = () => {
    // Group tasks by date
    const tasksByDate = filteredTasks.reduce((acc, task) => {
      const date = task.date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(task);
      return acc;
    }, {});

    // Get current month dates
    const currentMonth = currentCalendarDate.getMonth();
    const currentYear = currentCalendarDate.getFullYear();
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const today = new Date();

    const handlePrevMonth = () => {
      setCurrentCalendarDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const handleNextMonth = () => {
      setCurrentCalendarDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const handleToday = () => {
      setCurrentCalendarDate(new Date());
    };

    const handleDayClick = (dateStr) => {
      if (dateStr) {
        setSelectedDate(dateStr);
        setShowCalendarTaskDialog(true);
      }
    };

    const handleCreateCalendarTask = async () => {
      // Check if user can edit
      if (!canEdit()) {
        toast({ 
          title: 'Mode lecture seule', 
          description: 'Passez à la version payante pour créer des tâches',
          variant: 'destructive' 
        });
        return;
      }
      
      if (!calendarTaskName.trim() || !selectedDate) return;

      if (!currentWorkspaceId) {
        toast({ title: 'No workspace selected', variant: 'destructive' });
        return;
      }

      // Get first status column
      const currentStatuses = getCurrentStatusOptions();
      const firstStatus = currentStatuses[0] || { value: 'to_do', label: 'To Do', color: '#C4C4C4' };

      const newTaskObj = {
        boardId: selectedBoardId,
        task: calendarTaskName,
        assignees: [{ name: 'Unassigned', avatar: 'UN', color: '#CCCCCC' }],
        status: ensureValueField(firstStatus),
        priority: { value: 'medium', label: 'Medium', color: '#FDAB3D' },
        date: selectedDate,
        timeline: '',
        notes: ''
      };

      // Optimistic update - add immediately with temp ID
      const tempId = 'temp_' + Date.now();
      const optimisticTask = { 
        ...newTaskObj, 
        _id: tempId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      setTasks([...tasks, optimisticTask]);
      setCalendarTaskName('');
      setShowCalendarTaskDialog(false);
      setSelectedDate(null);

      try {
        const response = await axios.post(`${API}/tasks?workspace_id=${currentWorkspaceId}`, newTaskObj, { withCredentials: true });
        // Replace temp task with real one
        setTasks(prevTasks => prevTasks.map(task => task._id === tempId ? response.data : task));
      } catch (error) {
        // Remove temp task on error
        setTasks(prevTasks => prevTasks.filter(task => task._id !== tempId));
        console.error('Error creating task:', error);
        toast({ title: 'Error creating task', variant: 'destructive' });
      }
    };

    // Render Month View Content
    const renderMonthView = () => {
      const days = [];
      const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
      
      // Previous month days
      for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        days.push({ day: prevMonthLastDay - i, isCurrentMonth: false, date: null });
      }
      
      // Current month days
      for (let i = 1; i <= daysInMonth; i++) {
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
        days.push({ day: i, isCurrentMonth: true, date: dateStr });
      }

      return (
        <div className="grid grid-cols-7 gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 dark:text-gray-300 text-sm py-2">
                  {day}
                </div>
              ))}
              {days.map((dayObj, index) => {
                const dayTasks = dayObj.date ? (tasksByDate[dayObj.date] || []) : [];
                const isToday = dayObj.isCurrentMonth && 
                  dayObj.day === today.getDate() && 
                  currentMonth === today.getMonth() && 
                  currentYear === today.getFullYear();
                
                return (
                  <div
                    key={index}
                    onClick={() => dayObj.isCurrentMonth && handleDayClick(dayObj.date)}
                    className={`min-h-[120px] border rounded-lg p-2 transition-all ${
                      dayObj.isCurrentMonth ? 'bg-white hover:bg-blue-50 cursor-pointer' : 'bg-gray-50'
                    } ${isToday ? 'ring-2 ring-[#1C3E53]' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-2 ${
                      dayObj.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {dayObj.day}
                    </div>
                    <div className="space-y-1">
                      {dayTasks.slice(0, 3).map(task => (
                        <div
                          key={task._id}
                          className="text-xs p-1 rounded cursor-pointer hover:shadow-sm truncate transition-all"
                          style={{backgroundColor: `${task.status.color}20`, borderLeft: `3px solid ${task.status.color}`}}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTask(task);
                          }}
                          title={task.task}
                        >
                          {task.task}
                        </div>
                      ))}
                      {dayTasks.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-300 pl-1">
                          +{dayTasks.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
      );
    };

    // Render Day View
    if (calendarView === 'day') {
      const selectedDayStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(currentCalendarDate.getDate()).padStart(2, '0')}`;
      const dayTasks = tasksByDate[selectedDayStr] || [];
      const hours = Array.from({ length: 24 }, (_, i) => i);

      return (
        <div className="bg-white dark:bg-gray-800/95 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          {/* Day View Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {currentCalendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </h3>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentCalendarDate(new Date(currentYear, currentMonth, currentCalendarDate.getDate() - 1))}
                >
                  ←
                </Button>
                <Button variant="outline" size="sm" onClick={handleToday}>
                  Today
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCurrentCalendarDate(new Date(currentYear, currentMonth, currentCalendarDate.getDate() + 1))}
                >
                  →
                </Button>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCalendarView('month')}
            >
              Month View
            </Button>
          </div>

          {/* Day Timeline */}
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-4">
                <Calendar className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                <span className="font-medium text-gray-900 dark:text-gray-100">{dayTasks.length} tasks scheduled</span>
              </div>
              <Button
                size="sm"
                onClick={() => handleDayClick(selectedDayStr)}
                disabled={!canEdit()}
                className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canEdit() ? "Mode lecture seule" : ""}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </div>

            {/* Task List for the Day */}
            <div className="space-y-2">
              {dayTasks.length > 0 ? (
                dayTasks.map((task) => (
                  <div
                    key={task._id}
                    onClick={() => handleEditTask(task)}
                    className="p-4 border dark:border-gray-700 rounded-lg hover:shadow-md transition-all cursor-pointer"
                    style={{borderLeft: `4px solid ${task.status.color}`}}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{task.task}</h4>
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                              style={{backgroundColor: task.person.color}}
                            >
                              {task.person.avatar}
                            </div>
                            <span className="text-gray-600 dark:text-gray-300">{task.person.name}</span>
                          </div>
                          <span
                            className="px-2 py-1 rounded text-xs font-medium text-white"
                            style={{backgroundColor: task.priority.color}}
                          >
                            {task.priority.label}
                          </span>
                          <span
                            className="px-2 py-1 rounded text-xs font-medium text-white"
                            style={{backgroundColor: task.status.color}}
                          >
                            {task.status.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-300">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No tasks scheduled for this day</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDayClick(selectedDayStr)}
                    disabled={!canEdit()}
                    className="mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!canEdit() ? "Mode lecture seule" : ""}
                  >
                    Add Task
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // Render Week View Content
    const renderWeekView = () => {
      const weekDays = [];
      const startDate = new Date(currentWeekStart);
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        const dayTasks = filteredTasks.filter(task => task.date === dateStr);
        
        weekDays.push({
          date: dateStr,
          dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
          dayNum: date.getDate(),
          isToday: dateStr === new Date().toISOString().split('T')[0],
          tasks: dayTasks
        });
      }
      
      return (
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map(({ date, dayName, dayNum, isToday, tasks: dayTasks }) => (
            <div key={date} className="border dark:border-gray-700 rounded-lg p-3 min-h-[200px] flex flex-col">
              <div 
                className={`text-center mb-2 pb-2 border-b ${isToday ? 'text-[#1C3E53] font-bold' : 'text-gray-700'}`}
              >
                <div className="text-xs uppercase">{dayName}</div>
                <div className={`text-xl mt-1 ${isToday ? 'w-8 h-8 bg-[#1C3E53] text-white rounded-full flex items-center justify-center mx-auto' : ''}`}>
                  {dayNum}
                </div>
              </div>
              <div className="flex-1 space-y-1 overflow-y-auto">
                {dayTasks.map(task => (
                  <div
                    key={task._id}
                    className="text-xs p-2 rounded cursor-pointer hover:shadow-sm transition-all"
                    style={{backgroundColor: `${task.status.color}20`, borderLeft: `3px solid ${task.status.color}`}}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditTask(task);
                    }}
                    title={task.task}
                  >
                    <div className="font-medium truncate">{task.task}</div>
                    {task.budget && task.budget.amount && (
                      <div className="text-green-700 font-semibold mt-1">
                        {task.budget.currency === 'EUR' ? '€' : task.budget.currency === 'USD' ? '$' : '£'}
                        {task.budget.amount}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => handleDayClick(date)}
                className="mt-2 text-xs text-[#1C3E53] hover:bg-gray-50 dark:bg-gray-700 py-1 rounded transition-colors"
              >
                + Add
              </button>
            </div>
          ))}
        </div>
      );
    };

    // Main calendar wrapper with header and view switcher
    return (
      <>
        <div className="bg-white dark:bg-gray-800/95 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          {/* Calendar Header - Always visible */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {calendarMode === 'month' 
                  ? currentCalendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                  : `Week of ${currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                }
              </h3>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    if (calendarMode === 'month') {
                      handlePrevMonth();
                    } else {
                      const newDate = new Date(currentWeekStart);
                      newDate.setDate(newDate.getDate() - 7);
                      setCurrentWeekStart(newDate);
                    }
                  }}
                >
                  ←
                </Button>
                <Button variant="outline" size="sm" onClick={handleToday}>
                  Today
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    if (calendarMode === 'month') {
                      handleNextMonth();
                    } else {
                      const newDate = new Date(currentWeekStart);
                      newDate.setDate(newDate.getDate() + 7);
                      setCurrentWeekStart(newDate);
                    }
                  }}
                >
                  →
                </Button>
              </div>
            </div>
            {/* View Switcher - Always visible */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <Button
                variant={calendarMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarMode('month')}
                className={calendarMode === 'month' ? 'bg-white shadow-sm' : ''}
              >
                Month
              </Button>
              <Button
                variant={calendarMode === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarMode('week')}
                className={calendarMode === 'week' ? 'bg-white shadow-sm' : ''}
              >
                Week
              </Button>
            </div>
          </div>

          {/* Render the appropriate view */}
          {calendarMode === 'month' ? renderMonthView() : renderWeekView()}
        </div>

        {/* Calendar Task Creation Dialog */}
        <Dialog open={showCalendarTaskDialog} onOpenChange={setShowCalendarTaskDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Task</DialogTitle>
              <DialogDescription>
                Create a new task for {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="calendar-task-name">Task Name</Label>
                <Input
                  id="calendar-task-name"
                  placeholder="Enter task name..."
                  value={calendarTaskName}
                  onChange={(e) => setCalendarTaskName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateCalendarTask()}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCalendarTaskDialog(false);
                setCalendarTaskName('');
                setSelectedDate(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCalendarTask} 
                disabled={!canEdit()}
                className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1C3E53]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA] dark:bg-gray-900">
      {/* Subscription Banners */}
      {isExpired() && (
        <ReadOnlyBanner onUpgrade={() => navigate('/pricing')} />
      )}
      {isTrialActive() && (
        <TrialBanner onUpgrade={() => navigate('/pricing')} />
      )}
      
      {/* Top Header */}
      <header className="bg-white/95 dark:bg-gray-800/95 border-b border-gray-100 dark:border-gray-700/60 sticky top-0 z-40 backdrop-blur-sm shadow-sm">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <OptimizedImage 
                src="/doozum-logo.png" 
                alt="Doozum" 
                className="h-8 object-contain dark:invert dark:brightness-0 dark:contrast-200 cursor-pointer"
                priority={true}
                onClick={() => navigate('/')}
              />
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              
              {/* Workspace Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-gray-900 dark:text-gray-100 font-medium">
                    {currentWorkspace?.name || 'Select Workspace'}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {workspaces.map((workspace) => (
                    <DropdownMenuItem 
                      key={workspace._id}
                      onClick={() => handleWorkspaceChange(workspace._id)}
                      className={currentWorkspaceId === workspace._id ? 'bg-gray-100' : ''}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>{workspace.name}</span>
                        {workspace.owner_id === user?._id && (
                          <span className="text-xs text-gray-500 dark:text-gray-300">Owner</span>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowCreateWorkspace(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Workspace
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Members Button (only for owner/admin) */}
              {(userRole === 'owner' || userRole === 'admin') && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowMembersPanel(true)}
                  className="text-gray-600 dark:text-gray-300"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Members ({workspaceMembers.length})
                </Button>
              )}
              
              {/* Automations Button (only for owner/admin) */}
              {(userRole === 'owner' || userRole === 'admin') && selectedBoard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAutomationsPanel(true)}
                  className="text-gray-600 dark:text-gray-300"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Automations
                </Button>
              )}

              {/* Webhooks Button (only for owner/admin) */}
              {(userRole === 'owner' || userRole === 'admin') && selectedBoard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowWebhooksPanel(true)}
                  className="text-gray-600 dark:text-gray-300"
                >
                  <Webhook className="h-4 w-4 mr-2" />
                  Webhooks
                </Button>
              )}

              {/* Analytics Button */}
              {selectedBoard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAnalytics(true)}
                  className="text-gray-600 dark:text-gray-300"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Analytics
                </Button>
              )}

              {/* AI Assistant Button */}
              {selectedBoard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAIPanel(true)}
                  className="text-[#1C3E53] dark:text-[#4A90A4] font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  IA
                </Button>
              )}

              {/* Notes Button */}
              {selectedBoard && (
                <Button variant="ghost" size="sm" onClick={() => setShowNotesPanel(true)} className="text-gray-600 dark:text-gray-300">
                  <NotesIcon className="h-4 w-4 mr-2" />
                  Notes
                </Button>
              )}

              {/* Time Tracking Button */}
              {selectedBoard && (
                <Button variant="ghost" size="sm" onClick={() => setShowTimePanel(true)} className="text-gray-600 dark:text-gray-300">
                  <Clock className="h-4 w-4 mr-2" />
                  Temps
                </Button>
              )}

              {/* Contacts Button */}
              {currentWorkspaceId && (
                <Button variant="ghost" size="sm" onClick={() => navigate(`/contacts?workspace=${currentWorkspaceId}`)} className="text-gray-600 dark:text-gray-300">
                  <Users className="h-4 w-4 mr-2" />
                  Contacts
                </Button>
              )}

              {/* Share Button (owner/admin only) */}
              {selectedBoard && (userRole === 'owner' || userRole === 'admin') && (
                <Button variant="ghost" size="sm" onClick={handleShareBoard} className="text-gray-600 dark:text-gray-300">
                  <Share2 className="h-4 w-4 mr-2" />
                  Partager
                </Button>
              )}
              
              {/* Export Button */}
              {selectedBoard && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-gray-600 dark:text-gray-300"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Export
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => {
                        const url = `${API}/boards/${selectedBoard._id}/export/csv?workspace_id=${currentWorkspaceId}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => {
                        const url = `${API}/boards/${selectedBoard._id}/export/pdf?workspace_id=${currentWorkspaceId}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Export as PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => {
                        // Export as JSON
                        const dataToExport = {
                          board: {
                            name: selectedBoard.name,
                            type: selectedBoard.boardType,
                            statuses: selectedBoard.custom_statuses
                          },
                          workspace: currentWorkspace?.name,
                          tasks: tasks.map(t => ({
                            task: t.task,
                            status: t.status.label,
                            priority: t.priority.label,
                            assignees: t.assignees?.map(a => a.name) || [t.person?.name],
                            date: t.date,
                            timeline: t.timeline,
                            progress: t.progress,
                            channel: t.channel,
                            deal_value: t.deal_value,
                            company: t.company,
                            notes: t.notes
                          })),
                          exported_at: new Date().toISOString()
                        };
                        const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${selectedBoard.name.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.json`;
                        a.click();
                        toast({ title: 'JSON export downloaded' });
                      }}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Export as JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search everything"
                  className="pl-10 w-64 bg-[#F6F7FB] dark:bg-gray-900 border-none"
                />
              </div>
              
              {/* Notification Bell */}
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-[#1C3E53] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Theme Toggle */}
              <ThemeToggle />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-8 h-8 rounded-full bg-[#1C3E53] flex items-center justify-center text-white text-sm font-medium hover:bg-[#2A5470] transition-colors">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-300">{user?.email}</p>
                      {userRole && (
                        <span className="text-xs text-gray-400 capitalize">Role: {userRole}</span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  {user?.is_super_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/super-admin')} className="cursor-pointer text-purple-600 font-semibold">
                        <Shield className="mr-2 h-4 w-4" />
                        Super Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-64 bg-white dark:bg-gray-800/95 border-r dark:border-gray-700 min-h-[calc(100vh-57px)] p-4">
          <div className="space-y-1">
            {/* Dashboard Navigation */}
            <button
              onClick={() => {
                setShowChatView(false);
                navigate(`/dashboard?workspace=${currentWorkspaceId}`);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border dark:border-gray-700 border-gray-200 dark:border-gray-700"
            >
              <Home className="h-5 w-5" />
              <span className="text-sm font-medium">Dashboard</span>
            </button>
            
            {/* Chat Navigation */}
            <button
              onClick={() => {
                setShowChatView(true);
                setSelectedBoardId(null);
                // Update URL with view=chat parameter
                const newUrl = `/board?workspace=${currentWorkspaceId}&view=chat`;
                window.history.pushState({}, '', newUrl);
              }}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all ${
                showChatView
                  ? 'bg-[#1C3E53] text-white'
                  : 'hover:bg-gray-100 text-gray-700 border border-gray-200'
              }`}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Team Chat</span>
            </button>
            
            {/* GitHub Integration Button */}
            <button
              onClick={() => setShowGitHubPanel(true)}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border dark:border-gray-700 border-gray-200 dark:border-gray-700 mb-4"
            >
              <Github className="h-5 w-5" />
              <span className="text-sm font-medium">GitHub</span>
            </button>
            
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase px-3 py-2">My Boards</div>
            {boards.length === 0 ? (
              <div className="px-3 py-8 text-center text-gray-500 dark:text-gray-300 text-sm">
                No boards yet. Create one!
              </div>
            ) : (
              boards.map((board) => (
                <div key={board._id} className="group relative">
                  <button
                    onClick={() => {
                      const previousBoard = boards.find(b => b._id === selectedBoardId);
                      const newBoard = board;
                      
                      // Check if current view is compatible with new board
                      if (currentView === 'gantt' && newBoard.boardType !== 'project') {
                        setCurrentView('table'); // Reset to table view
                      } else if (currentView === 'pipeline' && newBoard.boardType !== 'sales') {
                        setCurrentView('table'); // Reset to table view
                      }
                      
                      setSelectedBoardId(board._id);
                      setShowChatView(false);
                      // Update URL to remove view=chat
                      const newUrl = `/board?workspace=${currentWorkspaceId}&board=${board._id}`;
                      window.history.pushState({}, '', newUrl);
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all ${
                      selectedBoardId === board._id
                        ? 'bg-[#1C3E53] text-white'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <BoardIcon icon={board.icon} size={20} />
                    <span className="text-sm font-medium flex-1 text-left truncate">{board.name}</span>
                  </button>
                  {/* Only owner can delete boards */}
                  {boards.length > 1 && userRole === 'owner' && (
                    <button
                      onClick={() => handleDeleteBoard(board._id)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                    >
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </button>
                  )}
                </div>
              ))
            )}
            {/* Only owner and admin can create boards */}
            {(userRole === 'owner' || userRole === 'admin') && (
              <button
                onClick={() => setShowBoardTypeSelector(true)}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#1C3E53] transition-all"
              >
                <Plus className="h-5 w-5" />
                <span className="text-sm font-medium">Add board</span>
              </button>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {showChatView ? (
            /* Chat View */
            <div className="h-[calc(100vh-120px)] bg-white dark:bg-gray-800/95 rounded-xl shadow-sm border dark:border-gray-700 overflow-hidden">
              <ChatIntegrated 
                workspaceId={currentWorkspaceId}
                userRole={userRole}
                workspaceMembers={workspaceMembers}
              />
            </div>
          ) : selectedBoard ? (
            <>
              {/* Board Header */}
              <div className="bg-white dark:bg-gray-800/95 rounded-xl shadow-sm border dark:border-gray-700 mb-6 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: `${selectedBoard?.color}20`}}>
                      <BoardIcon icon={selectedBoard?.icon} size={24} />
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedBoard?.name}</h1>
                      <p className="text-sm text-gray-500 dark:text-gray-300">{filteredTasks.length} items</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                      <Button
                        variant={currentView === 'table' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setCurrentView('table')}
                        className={`transition-all duration-200 ${
                          currentView === 'table' 
                            ? 'bg-white shadow-sm' 
                            : 'hover:bg-gray-200 hover:scale-110'
                        }`}
                      >
                        <Table className={`h-4 w-4 transition-colors ${
                          currentView === 'table' ? 'text-[#1C3E53]' : 'text-gray-600'
                        }`} />
                      </Button>
                      <Button
                        variant={currentView === 'kanban' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => {
                          console.log('Kanban button clicked, current view:', currentView);
                          setCurrentView('kanban');
                          console.log('setCurrentView called with kanban');
                        }}
                        className={`transition-all duration-200 ${
                          currentView === 'kanban' 
                            ? 'bg-white shadow-sm' 
                            : 'hover:bg-gray-200 hover:scale-110'
                        }`}
                      >
                        <Columns3 className={`h-4 w-4 transition-colors ${
                          currentView === 'kanban' ? 'text-[#1C3E53]' : 'text-gray-600'
                        }`} />
                      </Button>
                      <Button
                        variant={currentView === 'calendar' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setCurrentView('calendar')}
                        className={`transition-all duration-200 ${
                          currentView === 'calendar' 
                            ? 'bg-white shadow-sm' 
                            : 'hover:bg-gray-200 hover:scale-110'
                        }`}
                      >
                        <CalendarDays className={`h-4 w-4 transition-colors ${
                          currentView === 'calendar' ? 'text-[#1C3E53]' : 'text-gray-600'
                        }`} />
                      </Button>
                      
                      {/* Gantt View (Project boards only) */}
                      {selectedBoard?.boardType === 'project' && (
                        <Button
                          variant={currentView === 'gantt' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setCurrentView('gantt')}
                          className={`transition-all duration-200 ${
                            currentView === 'gantt' 
                              ? 'bg-white shadow-sm' 
                              : 'hover:bg-gray-200 hover:scale-110'
                          }`}
                          title="Gantt Timeline"
                        >
                          <BarChart3 className={`h-4 w-4 transition-colors ${
                            currentView === 'gantt' ? 'text-[#1C3E53]' : 'text-gray-600'
                          }`} />
                        </Button>
                      )}
                      
                      {/* Pipeline View (Sales boards only) */}
                      {selectedBoard?.boardType === 'sales' && (
                        <Button
                          variant={currentView === 'pipeline' ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setCurrentView('pipeline')}
                          className={`transition-all duration-200 ${
                            currentView === 'pipeline' 
                              ? 'bg-white shadow-sm' 
                              : 'hover:bg-gray-200 hover:scale-110'
                          }`}
                          title="Sales Pipeline"
                        >
                          <GitBranch className={`h-4 w-4 transition-colors ${
                            currentView === 'pipeline' ? 'text-[#1C3E53]' : 'text-gray-600'
                          }`} />
                        </Button>
                      )}
                    </div>
                    
                    {/* Filters Toggle Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                      className={`ml-3 ${showFilters ? 'bg-[#1C3E53] text-white hover:bg-[#E5602F]' : ''}`}
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                      {Object.keys(filters).filter(k => filters[k] && filters[k] !== 'all').length > 0 && (
                        <span className="ml-2 bg-white dark:bg-gray-800/95 text-[#1C3E53] px-1.5 py-0.5 rounded-full text-xs">
                          {Object.keys(filters).filter(k => filters[k] && filters[k] !== 'all').length}
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Board Metrics - Contextual Indicators */}
              {selectedBoard && (
                <BoardMetrics 
                  boardType={selectedBoard.boardType || 'project'} 
                  tasks={tasks} 
                />
              )}

              {/* Board Filters - Contextual Filters */}
              {showFilters && selectedBoard && (
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Filters</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFilters({});
                        setShowFilters(false);
                      }}
                    >
                      Clear all
                    </Button>
                  </div>
                  <BoardFilters
                    boardType={selectedBoard.boardType || 'project'}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                  />
                </div>
              )}

              {/* Loading indicator (non-blocking, top banner) */}
              {tasksLoading && (
                <div className="bg-orange-50 border dark:border-gray-700 border-orange-200 rounded-lg px-4 py-2 mb-4 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
                  <span className="text-sm text-orange-700">Loading tasks...</span>
                </div>
              )}

              {/* Board Content - Conditional Views (Always visible, no blocking loader) */}
              {currentView === 'table' && (
                    <div className="bg-white dark:bg-gray-800/95 rounded-xl shadow-sm border dark:border-gray-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-[#F6F7FB] dark:bg-gray-900 border-b dark:border-gray-700">
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[30%]">
                                Task
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[15%]">
                                Person
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[15%]">
                                Status
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[12%]">
                                Priority
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[15%]">
                                Date
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[10%]">
                                Budget
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[8%]">
                                Files
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider w-[3%]">
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTasks.map((task, index) => {
                              const subtaskProgress = getSubtaskProgress(task);
                              const hasSubtasks = subtaskProgress.total > 0;
                              const isExpanded = expandedTaskIds.has(task._id);
                              
                              return (
                                <React.Fragment key={task._id}>
                                  <tr className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors duration-100 group ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/30 dark:bg-gray-800/50'}`}>
                                    <td className="px-6 py-4">
                                      <div className="flex items-center space-x-2">
                                        {hasSubtasks && (
                                          <button
                                            onClick={() => toggleTaskExpansion(task._id)}
                                            className="text-gray-400 hover:text-gray-600 dark:text-gray-300"
                                          >
                                            <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                          </button>
                                        )}
                                        <div className="flex-1">
                                          {editingTaskId === task._id ? (
                                            <Input
                                              value={editingTaskName}
                                              onChange={(e) => setEditingTaskName(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveTaskName(task._id);
                                                if (e.key === 'Escape') cancelEditingTaskName();
                                              }}
                                              onBlur={() => saveTaskName(task._id)}
                                              autoFocus
                                              className="text-sm font-medium"
                                            />
                                          ) : (
                                            <div
                                              className="text-gray-900 dark:text-gray-100 font-medium cursor-pointer hover:text-[#1C3E53] transition-colors flex items-center gap-1.5"
                                              onClick={() => startEditingTaskName(task._id, task.task)}
                                              title="Click to edit task name"
                                            >
                                              {task.task}
                                              {task.recurrence && (
                                                <Repeat className="h-3 w-3 text-blue-500 flex-shrink-0" title={`Récurrent: ${task.recurrence.type}`} />
                                              )}
                                            </div>
                                          )}
                                          {hasSubtasks && (
                                            <div className="flex items-center space-x-2 mt-1">
                                              <div className="w-24 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                                <div
                                                  className="h-full bg-green-500 transition-all"
                                                  style={{width: `${subtaskProgress.percentage}%`}}
                                                />
                                              </div>
                                              <span className="text-xs text-gray-500 dark:text-gray-300">
                                                {subtaskProgress.completed}/{subtaskProgress.total}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                <td 
                                  className="px-6 py-4 cursor-pointer hover:bg-gray-100 dark:bg-gray-700"
                                  onClick={() => handleOpenAssignModal(task)}
                                >
                                  {/* Multiple Assignees Display */}
                                  {task.assignees && task.assignees.length > 0 ? (
                                    <div className="flex items-center">
                                      <div className="flex -space-x-2">
                                        {task.assignees.slice(0, 3).map((assignee, idx) => (
                                          <div
                                            key={idx}
                                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-white"
                                            style={{backgroundColor: assignee.color}}
                                            title={assignee.name}
                                          >
                                            {assignee.avatar}
                                          </div>
                                        ))}
                                        {task.assignees.length > 3 && (
                                          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-300 text-gray-700 dark:text-gray-300 text-xs font-medium border-2 border-white">
                                            +{task.assignees.length - 3}
                                          </div>
                                        )}
                                      </div>
                                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                                        {task.assignees.map(a => a.name).join(', ')}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center space-x-2">
                                      <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
                                        style={{backgroundColor: task.person?.color || '#CCCCCC'}}
                                      >
                                        {task.person?.avatar || 'UN'}
                                      </div>
                                      <span className="text-sm text-gray-700 dark:text-gray-300">{task.person?.name || 'Unassigned'}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <Select
                                    value={task.status.label}
                                    onValueChange={(value) => {
                                      const currentStatuses = getCurrentStatusOptions();
                                      const newStatus = currentStatuses.find(s => s.label === value);
                                      if (newStatus) handleStatusChange(task._id, newStatus);
                                    }}
                                  >
                                    <SelectTrigger
                                      className="border-none shadow-none font-medium text-white"
                                      style={{backgroundColor: task.status.color}}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {getCurrentStatusOptions().map((status) => (
                                        <SelectItem key={status.label} value={status.label}>
                                          <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded-full" style={{backgroundColor: status.color}}></div>
                                            <span>{status.label}</span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-6 py-4">
                                  <Select
                                    value={task.priority.label}
                                    onValueChange={(value) => {
                                      const newPriority = priorityOptions.find(p => p.label === value);
                                      if (newPriority) handlePriorityChange(task._id, newPriority);
                                    }}
                                  >
                                    <SelectTrigger
                                      className="border-none shadow-none font-medium text-white"
                                      style={{backgroundColor: task.priority.color}}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {priorityOptions.map((priority) => (
                                        <SelectItem key={priority.label} value={priority.label}>
                                          <div className="flex items-center space-x-2">
                                            <div className="w-3 h-3 rounded-full" style={{backgroundColor: priority.color}}></div>
                                            <span>{priority.label}</span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm">{new Date(task.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-4">
                                  <TimerButton taskId={task._id} onUpdate={() => {}} />
                                </td>
                                <td className="px-6 py-4">
                                  {task.budget && task.budget.amount ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                      {task.budget.currency === 'EUR' ? '€' : task.budget.currency === 'USD' ? '$' : '£'}
                                      {task.budget.amount.toLocaleString()}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  {task.attachments && task.attachments.length > 0 ? (
                                    <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-300">
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                      </svg>
                                      <span className="text-xs font-medium">{task.attachments.length}</span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleEditTask(task)}
                                      className="text-gray-400 hover:text-blue-500 transition-colors"
                                      title="Edit task"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTask(task._id)}
                                      className="text-gray-400 hover:text-red-500 transition-colors"
                                      title="Delete task"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              
                              {/* Expanded Subtasks Row */}
                              {isExpanded && hasSubtasks && (
                                <tr className="bg-gray-50 dark:bg-gray-700">
                                  <td colSpan="7" className="px-6 py-4">
                                    <div className="ml-8 space-y-2">
                                      {task.subtasks?.filter(s => !s.deleted).map((subtask) => (
                                        <div key={subtask.id} className="flex items-center space-x-3 p-2 bg-white dark:bg-gray-800/95 rounded border dark:border-gray-700">
                                          <button
                                            onClick={() => handleToggleSubtask(task._id, subtask.id, subtask.completed)}
                                            className="flex-shrink-0"
                                          >
                                            {subtask.completed ? (
                                              <CheckSquare className="h-5 w-5 text-green-600" />
                                            ) : (
                                              <Square className="h-5 w-5 text-gray-400" />
                                            )}
                                          </button>
                                          <div className="flex-1 flex items-center space-x-4">
                                            <span className={`text-sm ${subtask.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                              {subtask.title}
                                            </span>
                                            {subtask.assignee && (
                                              <div className="flex items-center space-x-1">
                                                <div
                                                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                                                  style={{backgroundColor: subtask.assignee.color}}
                                                >
                                                  {subtask.assignee.avatar}
                                                </div>
                                                <span className="text-xs text-gray-600 dark:text-gray-300">{subtask.assignee.name}</span>
                                              </div>
                                            )}
                                            {subtask.due_date && (
                                              <span className="text-xs text-gray-500 dark:text-gray-300">📅 {subtask.due_date}</span>
                                            )}
                                            {subtask.priority && (
                                              <span
                                                className="text-xs px-2 py-0.5 rounded font-medium"
                                                style={{backgroundColor: subtask.priority.color + '20', color: subtask.priority.color}}
                                              >
                                                {subtask.priority.label}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <button
                                              onClick={() => handleEditSubtask(task._id, subtask)}
                                              className="text-gray-400 hover:text-blue-500"
                                            >
                                              <Edit2 className="h-4 w-4" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteSubtask(task._id, subtask.id)}
                                              className="text-gray-400 hover:text-red-500"
                                            >
                                              <X className="h-4 w-4" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                            );
                          })}
                            {/* Add New Task Row */}
                            <tr className="border-b dark:border-gray-700">
                              <td className="px-6 py-4" colSpan="7">
                                <div className="flex items-center space-x-3">
                                  <Plus className="h-5 w-5 text-gray-400" />
                                  <Input
                                    placeholder="Add new task"
                                    value={newTask}
                                    onChange={(e) => setNewTask(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                                    className="border-none shadow-none focus:ring-0 text-gray-700 dark:text-gray-300"
                                  />
                                  {newTask && (
                                    <Button
                                      onClick={handleAddTask}
                                      size="sm"
                                      className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white text-white"
                                    >
                                      Add
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {currentView === 'kanban' && renderKanbanView()}

                  {currentView === 'calendar' && renderCalendarView()}

                  {currentView === 'gantt' && (
                    <GanttView
                      tasks={filteredTasks}
                      onTaskClick={(task) => {
                        setEditingTask({ ...task });
                        setShowEditDialog(true);
                        fetchComments(task._id);
                        fetchActivity(task._id);
                      }}
                    />
                  )}

                  {currentView === 'pipeline' && (
                    <PipelineView
                      tasks={filteredTasks}
                      stages={selectedBoard?.custom_statuses || []}
                      onTaskClick={(task) => {
                        setEditingTask({ ...task });
                        setShowEditDialog(true);
                        fetchComments(task._id);
                        fetchActivity(task._id);
                      }}
                      onTaskMove={(taskId, newStage) => {
                        handleTaskUpdate(taskId, { status: newStage });
                      }}
                    />
                  )}
            </>
          ) : (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">Welcome to Doozum!</p>
                <p className="text-gray-500 dark:text-gray-300">Your boards will appear here</p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Board Type Selector */}
      <BoardTypeSelector
        open={showBoardTypeSelector}
        onClose={() => setShowBoardTypeSelector(false)}
        onSelect={handleBoardTypeSelected}
      />

      {/* Create Board Dialog */}
      <Dialog open={showBoardDialog} onOpenChange={setShowBoardDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Board</DialogTitle>
            <DialogDescription>
              Add a new board to organize your tasks
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="board-name">Board Name</Label>
              <Input
                id="board-name"
                placeholder="e.g., Marketing Plan"
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateBoard()}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="board-icon">Icon</Label>
                <Select value={newBoardIcon} onValueChange={setNewBoardIcon}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <BoardIcon icon={newBoardIcon} size={18} />
                      <span>{BOARD_ICONS[newBoardIcon]?.label || 'Select Icon'}</span>
                    </div>
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {Object.entries(BOARD_ICONS).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <BoardIcon icon={key} size={18} />
                          <span>{label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="board-color">Color</Label>
                <div className="flex space-x-2">
                  {['#4A90A4', '#00C875', '#FDAB3D', '#E44258', '#A25DDC', '#579BFC'].map(color => (
                    <button
                      key={color}
                      onClick={() => setNewBoardColor(color)}
                      className={`w-8 h-8 rounded-full transition-all ${
                        newBoardColor === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                      }`}
                      style={{backgroundColor: color}}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBoardDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateBoard} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">Create Board</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Update task details, add comments, or view activity
            </DialogDescription>
          </DialogHeader>
          {editingTask && (
            <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="comments">
                  Comments {comments.length > 0 && `(${comments.length})`}
                </TabsTrigger>
                <TabsTrigger value="activity">
                  Activity {activityLog.length > 0 && `(${activityLog.length})`}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="task-name">Task Name</Label>
                <Input
                  id="task-name"
                  value={editingTask.task}
                  onChange={(e) => setEditingTask({...editingTask, task: e.target.value})}
                />
              </div>
              
              {/* Discussion Link */}
              {editingTask.discussion_link && (
                <div className="p-4 bg-orange-50 border dark:border-gray-700 border-orange-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <MessageCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-orange-900 mb-1">
                        💬 Created from chat discussion
                      </p>
                      <p className="text-xs text-orange-700 mb-2">
                        This task was created from a {editingTask.discussion_link.type === 'channel' ? 'channel' : 'direct'} message
                      </p>
                      <button
                        onClick={() => {
                          const view = editingTask.discussion_link.type === 'channel' ? 'chat' : 'chat';
                          navigate(`/board?workspace=${workspaceId}&board=${selectedBoard?._id}&view=${view}`);
                          setShowEditDialog(false);
                        }}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium underline flex items-center gap-1"
                      >
                        Go to original discussion →
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleOpenAssignModal(editingTask);
                    }}
                    className="w-full justify-start text-left h-10"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    {editingTask.assignees && editingTask.assignees.length > 0 
                      ? `${editingTask.assignees.length} assignee(s)`
                      : editingTask.person?.name || 'Click to assign'
                    }
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingTask.status.label}
                    onValueChange={(value) => {
                      const currentStatuses = getCurrentStatusOptions();
                      const newStatus = currentStatuses.find(s => s.label === value);
                      if (newStatus) setEditingTask({...editingTask, status: newStatus});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getCurrentStatusOptions().map((status) => (
                        <SelectItem key={status.label} value={status.label}>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full" style={{backgroundColor: status.color}}></div>
                            <span>{status.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={editingTask.priority.label}
                    onValueChange={(value) => {
                      const newPriority = priorityOptions.find(p => p.label === value);
                      if (newPriority) setEditingTask({...editingTask, priority: newPriority});
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map((priority) => (
                        <SelectItem key={priority.label} value={priority.label}>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full" style={{backgroundColor: priority.color}}></div>
                            <span>{priority.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <DateRangePicker
                  startDate={editingTask.timeline ? editingTask.timeline.split(' to ')[0]?.trim() : ''}
                  endDate={editingTask.timeline ? editingTask.timeline.split(' to ')[1]?.trim() : editingTask.date || ''}
                  onStartChange={(start) => {
                    const end = editingTask.timeline ? editingTask.timeline.split(' to ')[1]?.trim() : editingTask.date || '';
                    setEditingTask({...editingTask, timeline: start && end ? `${start} to ${end}` : ''});
                  }}
                  onEndChange={(end) => {
                    const start = editingTask.timeline ? editingTask.timeline.split(' to ')[0]?.trim() : '';
                    setEditingTask({...editingTask, timeline: start && end ? `${start} to ${end}` : '', date: end});
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-notes">Notes</Label>
                <Textarea
                  id="task-notes"
                  value={editingTask.notes}
                  onChange={(e) => setEditingTask({...editingTask, notes: e.target.value})}
                  placeholder="Add notes... (Press Enter for new line)"
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Recurrence */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Repeat className="h-3.5 w-3.5 text-blue-500" />
                  Répétition
                </Label>
                <Select
                  value={
                    editingTask.recurrence?.type
                      ? `${editingTask.recurrence.type}-${editingTask.recurrence.interval || 1}`
                      : 'none'
                  }
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setEditingTask({ ...editingTask, recurrence: null });
                    } else {
                      const [type, interval] = value.split('-');
                      setEditingTask({ ...editingTask, recurrence: { type, interval: parseInt(interval, 10) } });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ne se répète pas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ne se répète pas</SelectItem>
                    <SelectItem value="daily-1">Chaque jour</SelectItem>
                    <SelectItem value="weekly-1">Chaque semaine</SelectItem>
                    <SelectItem value="weekly-2">Toutes les 2 semaines</SelectItem>
                    <SelectItem value="monthly-1">Chaque mois</SelectItem>
                    <SelectItem value="yearly-1">Chaque année</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Attachments - Multi-file Upload */}
              <div className="space-y-2">
                <Label>Attachments ({(editingTask.attachments || []).length}/10)</Label>
                {editingTask.attachments && editingTask.attachments.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {editingTask.attachments.map((attachment) => (
                      <div key={attachment.id} className="relative group">
                        <OptimizedImage 
                          src={attachment.url} 
                          alt={attachment.name} 
                          className="w-full h-24 object-cover rounded-lg cursor-pointer"
                          onClick={() => window.open(attachment.url, '_blank')}
                          title={`${attachment.name} (${(attachment.size / 1024).toFixed(1)}KB)`}
                          lazy={true}
                          width={200}
                          height={96}
                        />
                        <button
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <div className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-60 text-white text-xs px-1 py-0.5 rounded truncate">
                          {attachment.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    id="file-upload-input"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleFileUpload(e.target.files);
                      }
                      e.target.value = '';
                    }}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label htmlFor="file-upload-input" className="flex-1 cursor-pointer">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="w-full" 
                      disabled={uploading}
                      asChild
                    >
                      <span>
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Upload Images (Max 10MB each)
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-300">Supported: JPG, PNG, WEBP • Max 10 images • Click to preview</p>
              </div>

              {/* Budget Field */}
              <div className="space-y-2">
                <Label>Budget (Optional)</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={editingTask.budget?.amount || ''}
                    onChange={(e) => {
                      const amount = e.target.value ? parseFloat(e.target.value) : null;
                      setEditingTask({
                        ...editingTask, 
                        budget: amount !== null ? { amount, currency: editingTask.budget?.currency || 'EUR' } : null
                      });
                    }}
                    className="col-span-2"
                  />
                  <Select
                    value={editingTask.budget?.currency || 'EUR'}
                    onValueChange={(currency) => {
                      if (editingTask.budget?.amount) {
                        setEditingTask({...editingTask, budget: { amount: editingTask.budget.amount, currency }});
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EUR">EUR €</SelectItem>
                      <SelectItem value="USD">USD $</SelectItem>
                      <SelectItem value="GBP">GBP £</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Contextual Fields Based on Board Type */}
              {selectedBoard?.boardType === 'marketing' && (
                <>
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold mb-3 text-[#1C3E53]">Marketing Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Channel</Label>
                        <Select
                          value={editingTask.channel || ''}
                          onValueChange={(value) => setEditingTask({...editingTask, channel: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select channel" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Social">Social Media</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                            <SelectItem value="Ads">Paid Ads</SelectItem>
                            <SelectItem value="SEO">SEO</SelectItem>
                            <SelectItem value="Event">Event</SelectItem>
                            <SelectItem value="Content">Content</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Performance Target</Label>
                        <Input
                          type="number"
                          placeholder="e.g., 1000 (reach/clicks)"
                          value={editingTask.performance || ''}
                          onChange={(e) => setEditingTask({...editingTask, performance: e.target.value ? parseFloat(e.target.value) : null})}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {selectedBoard?.boardType === 'sales' && (
                <>
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold mb-3 text-[#1C3E53]">Sales Details</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Company</Label>
                          <Input
                            placeholder="Company name"
                            value={editingTask.company || ''}
                            onChange={(e) => setEditingTask({...editingTask, company: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Deal Value (€)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="100"
                            placeholder="0"
                            value={editingTask.deal_value || ''}
                            onChange={(e) => setEditingTask({...editingTask, deal_value: e.target.value ? parseFloat(e.target.value) : null})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Expected Closing Date</Label>
                          <Input
                            type="date"
                            value={editingTask.closing_date || ''}
                            onChange={(e) => setEditingTask({...editingTask, closing_date: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Next Action</Label>
                          <Input
                            placeholder="e.g., Follow-up call"
                            value={editingTask.next_action || ''}
                            onChange={(e) => setEditingTask({...editingTask, next_action: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {selectedBoard?.boardType === 'project' && (
                <>
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold mb-3 text-[#1C3E53]">Project Details</h3>
                    <div className="space-y-2">
                      <Label>Progress (%)</Label>
                      <div className="flex items-center gap-3">
                        <Input
                          type="range"
                          min="0"
                          max="100"
                          value={editingTask.progress || 0}
                          onChange={(e) => setEditingTask({...editingTask, progress: parseInt(e.target.value)})}
                          className="flex-1"
                        />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-12 text-right">
                          {editingTask.progress || 0}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#1C3E53] transition-all duration-300"
                          style={{width: `${editingTask.progress || 0}%`}}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Subtasks Section */}
              <div className="pt-4 border-t space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Subtasks</Label>
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      {editingTask.subtasks?.filter(s => !s.deleted && s.completed).length || 0} / {editingTask.subtasks?.filter(s => !s.deleted).length || 0}
                    </span>
                  </div>
                  
                  {/* Progress Bar */}
                  {(() => {
                    const progress = getSubtaskProgress(editingTask);
                    return progress.total > 0 ? (
                      <div className="space-y-1">
                        <div className="w-full h-3 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 transition-all duration-300"
                            style={{width: `${progress.percentage}%`}}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-300 text-right">{progress.percentage}% complete</p>
                      </div>
                    ) : null;
                  })()}
                </div>

                {/* Subtasks List */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {editingTask.subtasks?.filter(s => !s.deleted).map((subtask) => (
                    <div key={subtask.id} className="flex items-start space-x-2 p-2 rounded hover:bg-gray-50 dark:bg-gray-700 group">
                      <button
                        onClick={() => handleToggleSubtask(editingTask._id, subtask.id, subtask.completed)}
                        className="mt-0.5"
                      >
                        {subtask.completed ? (
                          <CheckSquare className="h-5 w-5 text-green-600" />
                        ) : (
                          <Square className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className={`text-sm ${subtask.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {subtask.title}
                        </p>
                        <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500 dark:text-gray-300">
                          {subtask.assignee && (
                            <span>👤 {subtask.assignee.name}</span>
                          )}
                          {subtask.due_date && (
                            <span>📅 {subtask.due_date}</span>
                          )}
                          {subtask.priority && (
                            <span style={{color: subtask.priority.color}}>
                              🔥 {subtask.priority.label}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEditSubtask(editingTask._id, subtask)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 hover:text-blue-700"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSubtask(editingTask._id, subtask.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Subtask Input */}
                <div className="flex space-x-2">
                  <Input
                    placeholder="Add a subtask..."
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask(editingTask._id)}
                  />
                  <Button
                    size="sm"
                    onClick={() => handleAddSubtask(editingTask._id)}
                    disabled={addingSubtask || !newSubtaskTitle.trim()}
                    className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
                  >
                    {addingSubtask ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            </TabsContent>
              
              <TabsContent value="comments" className="flex-1 overflow-y-auto mt-4">
                <div className="space-y-4">
                  {/* Comment Input */}
                  <div className="flex space-x-2">
                    <div className="w-8 h-8 rounded-full bg-[#1C3E53] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 space-y-2 relative">
                      <Textarea
                        placeholder="Add a comment... (type @ to mention someone)"
                        value={newComment}
                        onChange={handleCommentChange}
                        onKeyDown={handleMentionKeyDown}
                        className="min-h-[80px]"
                      />
                      
                      {/* Mentions Dropdown */}
                      {showMentions && filteredMembers.length > 0 && (
                        <div className="absolute z-50 w-64 bg-white dark:bg-gray-800/95 border dark:border-gray-700 border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {filteredMembers.map((member, index) => (
                            <div
                              key={member.user_id}
                              onClick={() => handleSelectMention(member)}
                              className={`flex items-center space-x-3 px-3 py-2 cursor-pointer transition-colors ${
                                index === selectedMentionIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="w-8 h-8 rounded-full bg-[#4A90A4] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                {member.name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-300">{member.email}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <Button 
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                        size="sm"
                        className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Post Comment
                      </Button>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-4 mt-6">
                    {comments.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-300">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p>No comments yet. Be the first to comment!</p>
                      </div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment._id} className="flex space-x-3">
                          <div className="w-8 h-8 rounded-full bg-[#4A90A4] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                            {comment.user_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm">{comment.user_name}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-300">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              {editingCommentId === comment._id ? (
                                <div className="space-y-2">
                                  <Textarea
                                    value={editCommentText}
                                    onChange={(e) => setEditCommentText(e.target.value)}
                                    className="min-h-[60px]"
                                  />
                                  <div className="flex space-x-2">
                                    <Button 
                                      size="sm" 
                                      onClick={() => handleUpdateComment(comment._id)}
                                      className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
                                    >
                                      Save
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => {
                                        setEditingCommentId(null);
                                        setEditCommentText('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{comment.content}</p>
                              )}
                            </div>
                            {comment.user_id === user?._id && editingCommentId !== comment._id && (
                              <div className="flex space-x-2 mt-2">
                                <button
                                  onClick={() => {
                                    setEditingCommentId(comment._id);
                                    setEditCommentText(comment.content);
                                  }}
                                  className="text-xs text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:text-gray-300"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment._id)}
                                  className="text-xs text-red-500 hover:text-red-700"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="activity" className="flex-1 overflow-y-auto mt-4">
                <div className="space-y-3">
                  {activityLog.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-300">
                      <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No activity yet</p>
                    </div>
                  ) : (
                    activityLog.map((activity) => (
                      <div key={activity._id} className="flex space-x-3 py-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <Activity className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm">{activity.user_name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-300">
                              {activity.action_type === 'status_changed' && 'changed status'}
                              {activity.action_type === 'assignees_updated' && 'updated assignees'}
                              {activity.action_type === 'budget_updated' && 'updated budget'}
                              {activity.action_type === 'comment_added' && 'added a comment'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {activity.action_type === 'status_changed' && (
                              <span>
                                from <span className="font-medium">{activity.details.old_status}</span> to <span className="font-medium">{activity.details.new_status}</span>
                              </span>
                            )}
                            {activity.action_type === 'assignees_updated' && (
                              <span>
                                Assigned to: {activity.details.assignees?.join(', ')}
                              </span>
                            )}
                            {activity.action_type === 'budget_updated' && (
                              <span>
                                Budget: {activity.details.budget?.currency} {activity.details.budget?.amount}
                              </span>
                            )}
                            {activity.action_type === 'comment_added' && (
                              <span className="text-gray-500 dark:text-gray-300 italic">
                                "{activity.details.comment_preview}"
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 mt-1 block">
                            {new Date(activity.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter className="mt-4">
            <div className="flex justify-between w-full">
              <ArchiveButton 
                task={editingTask}
                variant="outline"
                onArchiveComplete={(taskId, isArchived) => {
                  setTasks(tasks.filter(t => t._id !== taskId));
                  setShowEditDialog(false);
                }}
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Close</Button>
                <Button onClick={handleSaveTaskEdit} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">Save Changes</Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter Panel */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Filter Tasks</SheetTitle>
            <SheetDescription>
              Filter tasks by status, assignee, or date range
            </SheetDescription>
          </SheetHeader>
          <div className="py-6 space-y-6">
            {/* Status Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Status</Label>
              <div className="space-y-2">
                {getCurrentStatusOptions().map((status) => (
                  <div key={status.label} className="flex items-center space-x-2">
                    <Checkbox
                      id={`status-${status.label}`}
                      checked={filters.status?.includes(status.label) || false}
                      onCheckedChange={(checked) => {
                        setFilters(prev => ({
                          ...prev,
                          status: checked 
                            ? [...(prev.status || []), status.label]
                            : (prev.status || []).filter(s => s !== status.label)
                        }));
                      }}
                    />
                    <label
                      htmlFor={`status-${status.label}`}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <div className="w-3 h-3 rounded-full" style={{backgroundColor: status.color}}></div>
                      <span className="text-sm">{status.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Assignee Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Assignee</Label>
              <div className="space-y-2">
                {uniqueAssignees.map((assignee) => (
                  <div key={assignee} className="flex items-center space-x-2">
                    <Checkbox
                      id={`assignee-${assignee}`}
                      checked={filters.assignee?.includes(assignee) || false}
                      onCheckedChange={(checked) => {
                        setFilters(prev => ({
                          ...prev,
                          assignee: checked 
                            ? [...(prev.assignee || []), assignee]
                            : (prev.assignee || []).filter(a => a !== assignee)
                        }));
                      }}
                    />
                    <label
                      htmlFor={`assignee-${assignee}`}
                      className="text-sm cursor-pointer"
                    >
                      {assignee}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Date Range</Label>
              <div className="space-y-2">
                <div>
                  <Label htmlFor="date-start" className="text-sm">Start Date</Label>
                  <Input
                    id="date-start"
                    type="date"
                    value={filters.dateRange?.start || ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: { ...(prev.dateRange || {}), start: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label htmlFor="date-end" className="text-sm">End Date</Label>
                  <Input
                    id="date-end"
                    type="date"
                    value={filters.dateRange?.end || ''}
                    onChange={(e) => setFilters(prev => ({
                      ...prev,
                      dateRange: { ...(prev.dateRange || {}), end: e.target.value }
                    }))}
                  />
                </div>
              </div>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={handleClearFilters}
                className="w-full"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Create Workspace Dialog */}
      <Dialog open={showCreateWorkspace} onOpenChange={setShowCreateWorkspace}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Create a workspace to organize your boards and collaborate with team members
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                placeholder="e.g., My Team Workspace"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateWorkspace()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateWorkspace(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkspace} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">Create Workspace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Member to Workspace</DialogTitle>
            <DialogDescription>
              Enter the email address of the person you want to invite
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="member@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleInviteMember()}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member - Can view and update assigned tasks</SelectItem>
                  <SelectItem value="admin">Admin - Can create boards and tasks, invite members</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button onClick={handleInviteMember} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">Create Invitation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation Success Modal */}
      <Dialog open={showInvitationSuccess} onOpenChange={(open) => {
        setShowInvitationSuccess(open);
        if (!open) {
          setInviteEmail('');
          setInviteRole('member');
          setInvitationLink('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invitation Created Successfully!</DialogTitle>
            <DialogDescription>
              Share this link with {inviteEmail} to join the workspace
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 font-medium">Invitation Link:</p>
              <div className="flex items-center space-x-2">
                <Input
                  value={invitationLink}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  onClick={handleCopyInviteLink}
                  size="sm"
                  className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white flex-shrink-0"
                >
                  Copy
                </Button>
              </div>
            </div>
            
            <div className="bg-blue-50 border dark:border-gray-700 border-b dark:border-gray-700lue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">📧 How it works:</p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>If they <strong>don't have an account</strong>: They'll create one and automatically join</li>
                <li>If they <strong>already have an account</strong>: They'll login and automatically join</li>
                <li>Link expires in <strong>7 days</strong></li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowInvitationSuccess(false)} className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace Members Panel */}
      <Sheet open={showMembersPanel} onOpenChange={setShowMembersPanel}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle>Workspace Members</SheetTitle>
            <SheetDescription>
              Manage members and their roles in {currentWorkspace?.name}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {/* Invite Button */}
            {(userRole === 'owner' || userRole === 'admin') && (
              <Button 
                onClick={() => {
                  setShowMembersPanel(false);
                  setShowInviteModal(true);
                }}
                className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Invite Member
              </Button>
            )}

            {/* Members List */}
            <div className="space-y-3">
              {workspaceMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-300">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No members yet</p>
                </div>
              ) : (
                workspaceMembers.map((member) => (
                  <div 
                    key={member._id} 
                    className="flex items-center justify-between p-4 border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:bg-gray-700 transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                        style={{backgroundColor: '#1C3E53'}}
                      >
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">{member.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-300">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Role Badge/Selector */}
                      {member.role === 'owner' ? (
                        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          Owner
                        </span>
                      ) : userRole === 'owner' ? (
                        <Select 
                          value={member.role} 
                          onValueChange={(newRole) => handleChangeMemberRole(member._id, newRole)}
                        >
                          <SelectTrigger className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          member.role === 'admin' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        </span>
                      )}
                      
                      {/* Remove Member Button */}
                      {canRemoveMember(member) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMember(member._id, member.name)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Role Explanation */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2 text-sm">
              <p className="font-semibold text-gray-900 dark:text-gray-100">Role Permissions:</p>
              <div className="space-y-1 text-gray-600 dark:text-gray-300">
                <p><span className="font-medium">Owner:</span> Full control - manage workspace, boards, tasks, and members</p>
                <p><span className="font-medium">Admin:</span> Create/edit boards and tasks, invite members</p>
                <p><span className="font-medium">Member:</span> View boards and update assigned tasks only</p>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>


      {/* Automations Panel */}
      <Sheet open={showAutomationsPanel} onOpenChange={setShowAutomationsPanel}>
        <SheetContent className="w-full sm:max-w-4xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Board Automations</SheetTitle>
            <SheetDescription>
              Automate your workflow with "When → Then" rules
            </SheetDescription>
          </SheetHeader>
          {selectedBoard && currentWorkspaceId && (
            <AutomationsTab
              boardId={selectedBoard._id}
              workspaceId={currentWorkspaceId}
              boardType={selectedBoard.boardType || 'project'}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Webhooks Panel */}
      <Sheet open={showWebhooksPanel} onOpenChange={setShowWebhooksPanel}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Webhooks</SheetTitle>
            <SheetDescription>
              Envoyez des notifications à des services externes quand des événements se produisent sur ce board.
            </SheetDescription>
          </SheetHeader>
          {selectedBoard && (
            <WebhooksSettings
              workspaceId={selectedBoard.workspace_id}
              boardId={selectedBoard._id}
              level="board"
              userRole={userRole}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Analytics Dashboard Panel */}
      <Sheet open={showAnalytics} onOpenChange={setShowAnalytics}>
        <SheetContent side="right" className="!w-[95vw] sm:!w-[95vw] !max-w-none p-0">
          <div className="h-full flex flex-col">
            <SheetHeader className="px-6 pt-6 pb-4 border-b dark:border-gray-700 bg-white dark:bg-gray-800/95">
              <SheetTitle className="text-2xl">Analytics Dashboard</SheetTitle>
              <SheetDescription>
                Performance insights and metrics for your board
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-6">
              {selectedBoard && (
                <AnalyticsDashboard 
                  boardType={selectedBoard.boardType || 'project'}
                  tasks={tasks}
                  boardName={selectedBoard.name}
                />
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>


      {/* Notes Panel */}
      <Sheet open={showNotesPanel} onOpenChange={setShowNotesPanel}>
        <SheetContent side="right" className="!w-[500px] sm:!w-[500px] !max-w-[500px] p-0 flex flex-col">
          {selectedBoard && <BoardNotes boardId={selectedBoard._id} />}
        </SheetContent>
      </Sheet>

      {/* Time Tracking Panel */}
      <Sheet open={showTimePanel} onOpenChange={setShowTimePanel}>
        <SheetContent side="right" className="!w-[420px] sm:!w-[420px] !max-w-[420px] p-0 flex flex-col">
          {selectedBoard && <TimeTracker boardId={selectedBoard._id} tasks={tasks} />}
        </SheetContent>
      </Sheet>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Partager ce board</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Ce lien donne accès en lecture seule à ce board. N'importe qui avec le lien peut le voir.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                readOnly
                value={shareLink}
                className="flex-1 text-xs font-mono bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-lg px-3 py-2 text-gray-700 dark:text-gray-300 focus:outline-none"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareLink);
                  setShareCopied(true);
                  setTimeout(() => setShareCopied(false), 2000);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#1C3E53] hover:bg-[#2A5470] text-white text-sm rounded-lg transition-colors"
              >
                {shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {shareCopied ? 'Copié !' : 'Copier'}
              </button>
            </div>
            <div className="flex justify-between">
              <button onClick={handleRevokeShare} className="text-sm text-red-500 hover:text-red-600">
                Révoquer le lien
              </button>
              <button onClick={() => setShowShareModal(false)} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Assistant Panel */}
      <Sheet open={showAIPanel} onOpenChange={setShowAIPanel}>
        <SheetContent side="right" className="!w-[420px] sm:!w-[420px] !max-w-[420px] p-0 flex flex-col">
          {selectedBoard && (
            <AIPanel
              board={selectedBoard}
              workspaceId={currentWorkspaceId}
              onClose={() => setShowAIPanel(false)}
              onTaskCreated={() => fetchTasks(selectedBoard._id)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* GitHub Integration Dialog */}
      <Dialog open={showGitHubPanel} onOpenChange={setShowGitHubPanel}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Github className="h-5 w-5" />
              GitHub Integration
            </DialogTitle>
            <DialogDescription>
              Connect your GitHub repositories to sync commits, issues, and PRs
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {currentWorkspaceId && (
              <GitHubIntegration workspaceId={currentWorkspaceId} />
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Task Assignment Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
            <DialogDescription>
              Select workspace members to assign to this task
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {workspaceMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-300">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No members in workspace</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {workspaceMembers.map((member) => {
                  const isSelected = selectedAssignees.some(a => a.name === member.name);
                  const memberColor = getMemberColor(member.name);
                  return (
                    <div
                      key={member._id}
                      onClick={() => handleToggleAssignee({...member, color: memberColor})}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-[#1C3E53] bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{backgroundColor: memberColor}}
                        >
                          {member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{member.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-300">{member.email}</p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-6 h-6 rounded-full bg-[#1C3E53] flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Selected Assignees Preview */}
            {selectedAssignees.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Selected ({selectedAssignees.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedAssignees.map((assignee, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full group"
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                        style={{backgroundColor: assignee.color}}
                      >
                        {assignee.avatar}
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{assignee.name}</span>
                      <button
                        onClick={() => {
                          setSelectedAssignees(selectedAssignees.filter(a => a.name !== assignee.name));
                        }}
                        className="w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove assignee"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={handleSaveAssignments} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">
              Save Assignments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications Panel */}
      <Sheet open={showNotifications} onOpenChange={setShowNotifications}>
        <SheetContent className="w-[400px] sm:w-[500px]">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle>Notifications</SheetTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllNotificationsRead}
                  className="text-xs"
                >
                  Mark all as read
                </Button>
              )}
            </div>
            <Tabs value={notificationFilter} onValueChange={setNotificationFilter} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="mentions">Mentions</TabsTrigger>
                <TabsTrigger value="assigned">Assigned</TabsTrigger>
              </TabsList>
            </Tabs>
          </SheetHeader>
          
          <div className="mt-6 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
            {notifications
              .filter(n => {
                if (notificationFilter === 'all') return true;
                if (notificationFilter === 'mentions') return n.type === 'mention';
                if (notificationFilter === 'assigned') return n.type === 'assignment';
                return true;
              })
              .map((notification) => (
                <div
                  key={notification._id}
                  className={`p-4 rounded-lg border transition-all duration-200 ${
                    notification.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50 hover:bg-blue-100 border-blue-200'
                  } ${notification.type === 'workspace_invitation' ? '' : 'cursor-pointer'}`}
                  onClick={() => {
                    if (notification.type !== 'workspace_invitation') {
                      handleMarkNotificationRead(notification._id);
                      if (notification.link) {
                        setShowNotifications(false);
                      }
                    }
                  }}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${
                      notification.type === 'mention' ? 'bg-purple-100' :
                      notification.type === 'assignment' ? 'bg-green-100' :
                      notification.type === 'workspace_invitation' ? 'bg-green-100' :
                      notification.type === 'invitation_accepted' ? 'bg-blue-100' :
                      'bg-gray-100'
                    }`}>
                      {notification.type === 'mention' ? (
                        <MessageSquare className="h-4 w-4 text-purple-600" />
                      ) : notification.type === 'assignment' ? (
                        <Users className="h-4 w-4 text-green-600" />
                      ) : notification.type === 'workspace_invitation' ? (
                        <Users className="h-4 w-4 text-green-600" />
                      ) : notification.type === 'invitation_accepted' ? (
                        <CheckSquare className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Bell className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{notification.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{notification.content}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(notification.createdAt).toLocaleString()}
                      </p>
                      
                      {/* Invitation Actions */}
                      {notification.type === 'workspace_invitation' && notification.related_id && (
                        <div className="flex items-center space-x-2 mt-3">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptInvitation(notification.related_id);
                              handleMarkNotificationRead(notification._id);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectInvitation(notification.related_id);
                              handleMarkNotificationRead(notification._id);
                            }}
                          >
                            Decline
                          </Button>
                        </div>
                      )}
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-[#1C3E53] rounded-full"></div>
                    )}
                  </div>
                </div>
              ))}
            
            {notifications.filter(n => {
              if (notificationFilter === 'all') return true;
              if (notificationFilter === 'mentions') return n.type === 'mention';
              if (notificationFilter === 'assigned') return n.type === 'assignment';
              return true;
            }).length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-300">
                <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit Subtask Modal */}
      <Dialog open={showSubtaskEdit} onOpenChange={setShowSubtaskEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subtask</DialogTitle>
          </DialogHeader>
          {editingSubtask && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editingSubtask.title}
                  onChange={(e) => setEditingSubtask({...editingSubtask, title: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Assignee</Label>
                <Select
                  value={editingSubtask.assignee?.name || '__unassigned__'}
                  onValueChange={(value) => {
                    if (value === '__unassigned__') {
                      setEditingSubtask({...editingSubtask, assignee: null});
                    } else {
                      const member = workspaceMembers.find(m => m.name === value);
                      setEditingSubtask({
                        ...editingSubtask,
                        assignee: member ? {
                          name: member.name,
                          avatar: member.name.charAt(0).toUpperCase(),
                          color: '#1C3E53'
                        } : null
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">Unassigned</SelectItem>
                    {workspaceMembers.map((member) => (
                      <SelectItem key={member.email} value={member.name}>
                        {member.name} ({member.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={editingSubtask.due_date || ''}
                  onChange={(e) => setEditingSubtask({...editingSubtask, due_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={editingSubtask.priority?.label || '__none__'}
                  onValueChange={(value) => {
                    if (value === '__none__') {
                      setEditingSubtask({...editingSubtask, priority: null});
                    } else {
                      const priorities = {
                        'Critical': {label: 'Critical', color: '#333333'},
                        'High': {label: 'High', color: '#401694'},
                        'Medium': {label: 'Medium', color: '#5559DF'},
                        'Low': {label: 'Low', color: '#579BFC'}
                      };
                      setEditingSubtask({...editingSubtask, priority: priorities[value] || null});
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editingSubtask.notes || ''}
                  onChange={(e) => setEditingSubtask({...editingSubtask, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubtaskEdit(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSubtask} className="bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation Popup */}
      <Dialog open={showInvitationPopup} onOpenChange={setShowInvitationPopup}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#1C3E53]" />
              Workspace Invitations
            </DialogTitle>
            <DialogDescription>
              You have been invited to join {pendingInvitations.length} workspace{pendingInvitations.length > 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {pendingInvitations.map((invitation) => (
              <div key={invitation._id} className="border dark:border-gray-700 rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-lg">{invitation.workspace_name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Invited by {invitation.invited_by} as <span className="font-medium">{invitation.role}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                    Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleAcceptInvitation(invitation._id)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Accept
                  </Button>
                  <Button
                    onClick={() => handleRejectInvitation(invitation._id)}
                    variant="outline"
                    className="flex-1"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowInvitationPopup(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Board;
